import { APIService, toCommaSeparated } from "@/services/api";
import type {
  JournalOptionTrade,
  JournalStockPosition,
  JournalTag,
  JournalTemplate,
  JournalReview,
  JournalStats,
  JournalCalendar,
  JournalCapitalLadder,
  JournalSummary,
  JournalOverview,
  JournalIBImportResult,
} from "@/models";
import type {
  ListJournalOptionTradesOptions,
  ListJournalOptionTradesResponse,
  CreateJournalOptionTradeRequest,
  UpdateJournalOptionTradeRequest,
  AssignJournalOptionTradeRequest,
  AssignJournalOptionTradeResponse,
  ListJournalStockPositionsOptions,
  ListJournalStockPositionsResponse,
  UpdateJournalStockPositionRequest,
  ListJournalTagsResponse,
  CreateJournalTagRequest,
  ListJournalTemplatesResponse,
  CreateJournalTemplateRequest,
  UpdateJournalTemplateRequest,
  ListJournalReviewsOptions,
  ListJournalReviewsResponse,
  CreateJournalReviewRequest,
  UpdateJournalReviewRequest,
  UpdateJournalTagRequest,
  JournalSettingsResponse,
  UpdateJournalSettingsRequest,
  JournalIBConfigResponse,
  UpdateJournalIBConfigRequest,
  ListJournalIBSyncRunsOptions,
  ListJournalIBSyncRunsResponse,
  ListJournalIBExecutionsOptions,
  ListJournalIBExecutionsResponse,
  ListJournalIBUnknownSymbolsResponse,
} from "@/modelsAPI";
import type {
  ListChatSessionsResponse,
  LoginRequest,
  RegisterTokenRequest,
  RegisterTokenResponse,
  RegisterRequest,
  RegisterResponse,
  CreateChatSessionRequest,
  UpdateChatSessionRequest,
  SendMessageRequest,
  SendMessageResponse,
  ListMessagesResponse,
  ListUsersResponse,
  ListCourseUsersResponse,
  CreateUserRequest,
  ListToolsResponse,
  UpdateCurrentUserRequest,
  AdminUpdateUserRequest,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  GetUserSubscriptionsResponse,
  CancelSubscriptionResponse,
  ListAllSubscriptionsResponse,
  GetSubscriptionEventsResponse,
  GetSubscriptionProductsResponse,
  CreateActiveSubscriptionRequest
} from "@/services/api.models";

import type {
  RecommendationCore,
  Course,
  CursorToken,
  RecommendationEarnings,
  StocksStatusLight,
  RecommendationSwingtrade,
  SwingTradeAdjustement,
  LongTermRecommendation,
  LongTermAdjustement,
  RecommendationStatsOverview,
  SubscriptionType,
  SubscriptionProductType,
  SubscriptionPriceProduct,
  RecommendationCreateStats, SubscriptionStatus
} from "@/models";
import type {
    CourseID,
    ModuleID,
    VideoID,
    VideoAttachmentID,
    MailID,
    DiscordMessageID,
    TelegramMessageID,
    SCNMessageID,
    PushNotificationID,
    Video, Module, VideoAttachment,
    ListVideoAttachmentsOptions,
    ListVideoAttachmentsResponse,
    UpdateVideoAttachmentRequest,
    Mail,
    ListMailsOptions,
    ListMailsResponse,
    MailQueueStats,
    SetMailToFailedRequest,
    SendTestMailRequest,
    DiscordMessage,
    ListDiscordMessagesOptions,
    ListDiscordMessagesResponse,
    SendDiscordMessageRequest,
    TelegramMessage,
    ListTelegramMessagesOptions,
    ListTelegramMessagesResponse,
    SendTelegramMessageRequest,
    SCNMessage,
    ListSCNMessagesOptions,
    ListSCNMessagesResponse,
    SendSCNMessageRequest,
    PushNotification,
    ListPushNotificationsOptions,
    ListPushNotificationsResponse,
    SendPushNotificationRequest,
    CourseMailSequence,
    UpdateCourseMailSequenceRequest,
    UserCourseProgressResponse,
    UserModuleProgressResponse
} from "@/models";
import type {
    ChatSession, ChatSessionType, ErrorLog, ErrorLogSortField, ExerrMethod, Job,
    JobExecution, JobExecutionSortField,
    JobLogID, JobLogLevel,
    JobLogSortField, JobStatus,
    JobType, SortDirection, Symbol, SymbolID, StockExchange, StockExchangeID,
    UserQuestion, UserQuestionID,
    CoreRecommendationID, EarningsRecommendationID, SwingTradeID, SwingTradeAdjustementID, LongTermRecommendationID, LongTermAdjustementID,
    RecommendationFavorite, RecommendationFavoriteID,
    User, UserID, SubscriptionID,
    Event, EventID,
    EventSeries, EventSeriesID, EventSeriesScope, EventImportResult,
    PermissionGroup, FeatureType
} from "@/models";
import type {DataBlob} from "@/models";
import type { Scratchpad } from "@/models";
import type { VideoKnowledge } from "@/models";
import type {ToolExecutionRequest, ToolExecutionResponse} from "@/modelsLocal";
import {
  type AckErrorsResponse,
  type AddSwingTradeAdjustementRequest,
  type AddLongTermAdjustementRequest,
  type EmailOptOutResponse,
  type CreateCoreRecommendationsResponse,
  type CreateCourseRequest,
  type CreateEarningsRecommendationsResponse,
  type CreateLongTermTradesResponse,
  type CreateModuleRequest,
  type CreateStocksStatusLightRequest,
  type CreateSwingTradesResponse,
  type CreateVideoRequest,
  type FinishLongTermTradeRequest,
  type FinishSwingTradeRequest,
  type GetCourseProgressResponse,
  type GetDashboardResponse,
  type GetErrorTypesResponse,
  type GetModuleProgressResponse,
  type ListCoreRecommendationsOptions,
  type ListCoreRecommendationsResponse,
  type ListCoursesOptions,
  type ListCoursesResponse,
  type ListEarningsRecommendationsOptions,
  type ListEarningsRecommendationsResponse,
  type ListErrorsResponse,
  type ListJobExecutionsResponse,
  type ListJobLogsResponse,
  type ListJobsResponse,
  type ListModulesOptions,
  type ListModulesResponse,
  type ListAllModulesOptions,
  type ListAllModulesResponse,
  type ListStocksStatusLightsOptions,
  type ListStocksStatusLightsResponse,
  type ListLongTermAdjustementsResponse,
  type ListLongTermTradesOptions,
  type ListLongTermTradesResponse,
  type ListSwingTradeAdjustementsResponse,
  type ListSwingTradesOptions,
  type ListSwingTradesResponse,
  type ListSymbolHistoryResponse,
  type ListSymbolsOptions,
  type ListSymbolsResponse,
  type GetSymbolGraphResponse,
  type ListVideosOptions,
  type ListVideosResponse,
  type ListAllVideosOptions,
  type ListAllVideosResponse,
  type UpdateCoreRecommendationRequest,
  type UpdateCourseRequest,
  type UpdateEarningsRecommendationRequest,
  type UpdateLongTermAdjustementRequest,
  type UpdateLongTermTradeRequest,
  type UpdateModuleRequest,
  type UpdateSwingTradeAdjustementRequest,
  type UpdateSwingTradeRequest,
  type UpdateVideoProgressRequest,
  type UpdateVideoProgressResponse,
  type UpdateVideoRequest,
  type ListEventsOptions,
  type ListEventsResponse,
  type CreateEventRequest,
  type UpdateEventRequest,
  type ListEventSeriesOptions,
  type ListEventSeriesResponse,
  type CreateEventSeriesRequest,
  type UpdateEventSeriesRequest,
  type EventSeriesMutationResponse,
  type UpdateEventSeriesResponse,
  type CreateBatchCoreRecommendationRequest,
  type CreateBatchEarningsRecommendationRequest,
  type CreateBatchSwingTradeRequest,
  type CreateBatchLongTermTradeRequest,
  type ListPreviewCoreRecommendationsOptions,
  type ListPreviewCoreRecommendationsResponse,
  type ListPreviewEarningsRecommendationsOptions,
  type ListPreviewEarningsRecommendationsResponse,
  type ListPreviewSwingTradesOptions,
  type ListPreviewSwingTradesResponse,
  type ListPreviewLongTermTradesOptions,
  type ListPreviewLongTermTradesResponse,
  type CreateFavoriteRequest,
  type UpdateFavoriteRequest,
  type ListFavoritesOptions,
  type ListFavoritesResponse,
  type CreateStockExchangeRequest,
  type UpdateStockExchangeRequest,
  type ListStockExchangesOptions,
  type ListStockExchangesResponse,
  type CreateUserQuestionRequest,
  type UpdateUserQuestionRequest,
  type ListUserQuestionsOptions,
  type ListUserQuestionsResponse,
  type RecalcAllRecommendationsResponse,
  type ResendNotificationsRequest,
  type ResendCoreRecommendationsResponse,
  type ResendEarningsRecommendationsResponse,
  type ResendSwingTradesResponse,
  type ResendLongTermTradesResponse,
  type ListDayTradingSessionsOptions,
  type ListDayTradingSessionsResponse,
  type CreateDayTradingSessionRequest,
  type UpdateDayTradingSessionRequest,
  type DayTradingStreamStatusResponse,
  type CreateDayTradeRequest,
  type UpdateDayTradeRequest,
  type CreateDayTradeAdjustementRequest,
  type UpdateDayTradeAdjustementRequest,
  type UpdateDayTradingParticipantStateRequest,
  type AddExternalErrorRequest,
  type AddExternalErrorResponse,
  type ListOptionContractsOptions,
  type ListOptionContractsResponse,
  type ListOptionUnderlyingsResponse
} from "@/modelsAPI";
import type {
  DayTradingSessionID,
  DayTradingSession,
  DayTradingParticipant,
  DayTradeID,
  DayTradeAdjustementID,
  DayTradingParticipantID,
  DayTradingChatMessageID,
  DayTradingChatReactionID
} from "@/models";
import type { OptionContract, OptionContractID } from "@/models";

// An IB flex statement is only delivered ~30s after it was requested, and the sync
// endpoint waits that out server-side.
const JOURNAL_IB_SYNC_TIMEOUT_MS = 180_000;

export class APIRoutes {
  static setToken(token: string) {
    return APIService.inst.setToken(token);
  }

  static isAuthenticated(): boolean {
    return APIService.inst.isAuthenticated();
  }

  static async addExternalError(request: AddExternalErrorRequest): Promise<AddExternalErrorResponse> {
    return APIService.inst.apiFetch<AddExternalErrorRequest, AddExternalErrorResponse>('POST', '/errors', {
        body: request,
        auth: false
    });
  }

  static async login(mail: string, pass: string): Promise<{token: string}> {
    return APIService.inst.apiFetch<LoginRequest, {token: string}>('POST', '/auth/login', {
        body: {email: mail, password: pass},
        auth: false
    });
  }

  static async registerToken(request: RegisterTokenRequest): Promise<RegisterTokenResponse> {
    return APIService.inst.apiFetch<RegisterTokenRequest, RegisterTokenResponse>('POST', '/auth/register-token', {
        body: request,
        auth: false
    });
  }

  static async register(request: RegisterRequest): Promise<RegisterResponse> {
    return APIService.inst.apiFetch<RegisterRequest, RegisterResponse>('POST', '/auth/register', {
        body: request,
        auth: false
    });
  }

  static async requestPasswordReset(email: string): Promise<void> {
    return APIService.inst.apiFetch<{email: string}, void>('POST', '/auth/request-forgot-pw-mail', {
        body: {email},
        auth: false
    });
  }

  static async resetPassword(uid: UserID, newPassword: string, token: string): Promise<User> {
    return APIService.inst.apiFetch<{newPassword: string, token: string}, User>('PATCH', `/users/${uid}/password`, {
        body: {newPassword, token},
        auth: false
    });
  }

  static async refreshToken(): Promise<{token: string}> {
    // Bypass the auth: true path on purpose: the apiFetch pre-flight calls
    // refreshTokenIfNeeded(), which would recurse into this method.
    const token = APIService.inst.getToken();
    if (token === null) throw new Error('Cannot refresh token without an existing token');
    return APIService.inst.apiFetch<void, {token: string}>('POST', '/auth/refresh', {
        auth: false,
        headers: { Authorization: `Bearer ${token}` }
    });
  }

  static logout(): void {
    APIService.inst.removeToken()
  }

  static setOnUnauthorized(callback: () => void): void {
    APIService.inst.setOnUnauthorized(callback)
  }

  static async getCurrentUser(): Promise<User> {
    return APIService.inst.apiFetch<void, User>('GET', '/users/self', {
      auth: true
    });
  }

  static async updateCurrentUser(request: UpdateCurrentUserRequest): Promise<User> {
    return APIService.inst.apiFetch<UpdateCurrentUserRequest, User>('PATCH', '/users/self', {
      body: request,
      auth: true
    });
  }

  static async emailOptOut(userId: UserID, type: string, key: string): Promise<EmailOptOutResponse> {
    return APIService.inst.apiFetch<void, EmailOptOutResponse>('POST', '/users/:uid/email-opt-out/:type/:key', {
      urlParams: { uid: userId, type: type, key: key },
      auth: false
    });
  }

  static async listChatSessions(options?: {
    userID?: string;
    limit?: number;
    next?: CursorToken;
    sort?: 'created' | 'updated' | 'title';
    sortDirection?: SortDirection;
    type?: ChatSessionType;
  }): Promise<ListChatSessionsResponse> {
    const queryParams = {
      limit: options?.limit,
      next: options?.next,
      sort: options?.sort,
      sortDirection: options?.sortDirection,
      type: options?.type
    };

    if (options?.userID) {
      return APIService.inst.apiFetch<void, ListChatSessionsResponse>('GET', '/users/:uid/sessions', {
        urlParams: { uid: options.userID },
        queryParams,
        auth: true
      });
    } else {
      return APIService.inst.apiFetch<void, ListChatSessionsResponse>('GET', '/users/self/sessions', {
        queryParams,
        auth: true
      });
    }
  }

  static async createChatSession(request: CreateChatSessionRequest): Promise<ChatSession> {
    return APIService.inst.apiFetch<CreateChatSessionRequest, ChatSession>('POST', '/users/self/sessions', {
        body: request,
        auth: true
    });
  }

  static async getChatSession(sessionId: string): Promise<ChatSession> {
    return APIService.inst.apiFetch<void, ChatSession>('GET', '/users/self/sessions/:sid', {
        urlParams: { sid: sessionId },
        auth: true
    });
  }

  static async updateChatSession(sessionId: string, request: UpdateChatSessionRequest): Promise<ChatSession> {
    return APIService.inst.apiFetch<UpdateChatSessionRequest, ChatSession>('PATCH', '/users/self/sessions/:sid', {
        urlParams: { sid: sessionId },
        body: request,
        auth: true
    });
  }

  static async deleteChatSession(sessionId: string): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/self/sessions/:sid', {
        urlParams: { sid: sessionId },
        auth: true
    });
  }

  static async closeChatSession(sessionId: string): Promise<ChatSession> {
    return APIService.inst.apiFetch<void, ChatSession>('POST', '/users/self/sessions/:sid/close', {
        urlParams: { sid: sessionId },
        auth: true
    });
  }

  static async sendMessage(sessionId: string, request: SendMessageRequest): Promise<SendMessageResponse> {
    return APIService.inst.apiFetch<SendMessageRequest, SendMessageResponse>('POST', '/users/self/sessions/:sid/messages', {
        urlParams: { sid: sessionId },
        body: request,
        auth: true
    });
  }

  static async listMessages(sessionId: string, after?: string, all?: boolean): Promise<ListMessagesResponse> {
    return APIService.inst.apiFetch<void, ListMessagesResponse>('GET', '/users/self/sessions/:sid/messages', {
        urlParams: { sid: sessionId },
        queryParams: { after, all: all ? 'true' : undefined },
        auth: true
    });
  }

  static async listAllChatSessions(options?: {
      limit?: number;
      next?: CursorToken;
      sort?: (keyof Symbol);
      sortDirection?: SortDirection;
  }): Promise<ListChatSessionsResponse> {
    return APIService.inst.apiFetch<void, ListChatSessionsResponse>('GET', '/sessions', {
        queryParams: {
            limit: options?.limit,
            next: options?.next,
            sort: options?.sort,
            sortDirection: options?.sortDirection
        },
        auth: true
    });
  }

  static async listDataBlobs(): Promise<DataBlob[]> {
    return APIService.inst.apiFetch<void, DataBlob[]>('GET', '/blobs', {
      auth: true
    });
  }

  static async getDataBlob(blobId: string): Promise<DataBlob> {
    return APIService.inst.apiFetch<void, DataBlob>('GET', '/blobs/:bid', {
        urlParams: { bid: blobId },
        auth: true
    });
  }

  static async deleteDataBlob(blobId: string): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/blobs/:bid', {
        urlParams: { bid: blobId },
        auth: true
    });
  }

  static getBlobContentUrl(blobId: string): string {
    const token = APIService.inst.getToken();
    const baseUrl = import.meta.env.VITE_API_URL;
    return `${baseUrl}/blobs/${blobId}/content?auth=${token}`;
  }

  static async listUsers(options?: {
      limit?: number;
      next?: CursorToken;
      sort?: (keyof User);
      sortDirection?: SortDirection;
      search?: string;
      permissionGroup?: PermissionGroup | PermissionGroup[];
      featureType?: FeatureType | FeatureType[];
      activeSubscriptionTypes?: SubscriptionType | SubscriptionType[];
      activeSubscriptionProductTypes?: SubscriptionProductType | SubscriptionProductType[];
  }): Promise<ListUsersResponse> {
    return APIService.inst.apiFetch<void, ListUsersResponse>('GET', '/users', {
      queryParams: {
          limit: options?.limit,
          next: options?.next,
          sort: options?.sort,
          sortDirection: options?.sortDirection,
          search: options?.search,
          permissionGroup: toCommaSeparated(options?.permissionGroup),
          featureType: toCommaSeparated(options?.featureType),
          activeSubscriptionTypes: toCommaSeparated(options?.activeSubscriptionTypes),
          activeSubscriptionProductTypes: toCommaSeparated(options?.activeSubscriptionProductTypes)
      },
      auth: true
    });
  }

  static async createUser(request: CreateUserRequest): Promise<User> {
    return APIService.inst.apiFetch<CreateUserRequest, User>('POST', '/users', {
      body: request,
      auth: true
    });
  }

  static async getUser(userId: string): Promise<User> {
    return APIService.inst.apiFetch<void, User>('GET', '/users/:uid', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async updateUser(userId: string, request: AdminUpdateUserRequest): Promise<User> {
    return APIService.inst.apiFetch<AdminUpdateUserRequest, User>('PATCH', '/users/:uid', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async deleteUser(userId: UserID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async exportUsers(): Promise<void> {
    const url = APIService.inst.buildUrl('/users/export');

    const token = APIService.inst.getToken();
    const headers: Record<string, string> = {};
    if (token !== null) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export fehlgeschlagen: ${errorText}`);
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'users-export.xls';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  }

  static async listTools(): Promise<ListToolsResponse> {
    return APIService.inst.apiFetch<void, ListToolsResponse>('GET', '/tools', {
      auth: true
    });
  }

  static async executeTool(request: ToolExecutionRequest): Promise<ToolExecutionResponse> {
    return APIService.inst.apiFetch<ToolExecutionRequest, ToolExecutionResponse>('POST', '/tools/execute', {
      body: request,
      auth: true
    });
  }

  // Admin endpoints
  static async getUserScratchpad(userId: string): Promise<Scratchpad> {
    return APIService.inst.apiFetch<void, Scratchpad>('GET', '/users/:uid/scratchpad', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async getUserCourseProgress(userId: string, courseId: CourseID): Promise<UserCourseProgressResponse> {
    return APIService.inst.apiFetch<void, UserCourseProgressResponse>('GET', '/users/:uid/courses/:courseId/progress', {
      urlParams: { uid: userId, courseId },
      auth: true
    });
  }

  // Job management endpoints
  static async listJobs(): Promise<ListJobsResponse> {
    return APIService.inst.apiFetch<void, ListJobsResponse>('GET', '/jobs', {
      auth: true
    });
  }

  static async getJob(jobType: JobType): Promise<Job> {
    return APIService.inst.apiFetch<void, Job>('GET', '/jobs/:jtype', {
      urlParams: { jtype: jobType },
      auth: true
    });
  }

  static async runJob(jobType: JobType): Promise<{ message: string }> {
    return APIService.inst.apiFetch<void, { message: string }>('POST', '/jobs/:jtype/run', {
      urlParams: { jtype: jobType },
      auth: true
    });
  }

  static async listJobExecutions(
    jobType: JobType,
    options?: {
      limit?: number;
      next?: CursorToken;
      sort?: JobExecutionSortField;
      sortDirection?: SortDirection;
      status?: JobStatus;
      hasError?: boolean;
      hasChanges?: boolean;
      hasDebugLogs?: boolean;
      hasInfoLogs?: boolean;
      hasWarnLogs?: boolean;
      hasErrorLogs?: boolean;
      hasFatalLogs?: boolean;
      hasWarnOrHigherLogs?: boolean;
      hasErrorOrHigherLogs?: boolean;
      hasFastRerun?: boolean;
    }
  ): Promise<ListJobExecutionsResponse> {
    return APIService.inst.apiFetch<void, ListJobExecutionsResponse>('GET', '/jobs/:jtype/executions', {
      urlParams: { jtype: jobType },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        status: options?.status,
        hasError: options?.hasError,
        hasChanges: options?.hasChanges,
        hasDebugLogs: options?.hasDebugLogs,
        hasInfoLogs: options?.hasInfoLogs,
        hasWarnLogs: options?.hasWarnLogs,
        hasErrorLogs: options?.hasErrorLogs,
        hasFatalLogs: options?.hasFatalLogs,
        hasWarnOrHigherLogs: options?.hasWarnOrHigherLogs,
        hasErrorOrHigherLogs: options?.hasErrorOrHigherLogs,
        hasFastRerun: options?.hasFastRerun
      },
      auth: true
    });
  }

  static async getJobExecution(jobType: JobType, executionId: string): Promise<JobExecution> {
    return APIService.inst.apiFetch<void, JobExecution>('GET', '/jobs/:jtype/executions/:execid', {
      urlParams: { jtype: jobType, execid: executionId },
      auth: true
    });
  }

  static async listJobLogs(
    jobType: JobType,
    executionId: string,
    options?: {
      limit?: number;
      next?: CursorToken;
      sort?: JobLogSortField;
      sortDirection?: SortDirection;
      level?: JobLogLevel;
      after?: JobLogID; // only return logs after this log - used to tail the logs of a running execution
    }
  ): Promise<ListJobLogsResponse> {
    return APIService.inst.apiFetch<void, ListJobLogsResponse>('GET', '/jobs/:jtype/executions/:execid/logs', {
      urlParams: { jtype: jobType, execid: executionId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        level: options?.level,
        after: options?.after
      },
      auth: true
    });
  }

  // Error management endpoints
  static async listErrors(
    options?: {
      limit?: number;
      next?: CursorToken;
      sort?: ErrorLogSortField;
      sortDirection?: SortDirection;
      acknowledged?: boolean;
      category?: string;
      listenerMethod?: ExerrMethod;
      notificationSent?: boolean;
      search?: string;
      severity?: string;
      type?: string;
    }
  ): Promise<ListErrorsResponse> {
    return APIService.inst.apiFetch<void, ListErrorsResponse>('GET', '/errors', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        acknowledged: options?.acknowledged,
        category: options?.category,
        listenerMethod: options?.listenerMethod,
        notificationSent: options?.notificationSent,
        search: options?.search,
        severity: options?.severity,
        type: options?.type
      },
      auth: true
    });
  }

  static async getError(errorId: string): Promise<ErrorLog> {
    return APIService.inst.apiFetch<void, ErrorLog>('GET', '/errors/:id', {
      urlParams: { id: errorId },
      auth: true
    });
  }

  static async acknowledgeError(errorId: string, note?: string): Promise<ErrorLog> {
    return APIService.inst.apiFetch<{note?: string}, ErrorLog>('POST', '/errors/:id/ack', {
      urlParams: { id: errorId },
      body: { note: note || '' },
      auth: true
    });
  }

  static async acknowledgeMultipleErrors(
    note: string,
    filters?: {
      acknowledged?: boolean;
      category?: string;
      listenerMethod?: ExerrMethod;
      notificationSent?: boolean;
      search?: string;
      severity?: string;
      type?: string;
    }
  ): Promise<AckErrorsResponse> {
    return APIService.inst.apiFetch<{note: string}, AckErrorsResponse>('POST', '/errors/ack-multiple', {
      body: { note },
      queryParams: {
        acknowledged: filters?.acknowledged,
        category: filters?.category,
        listenerMethod: filters?.listenerMethod,
        notificationSent: filters?.notificationSent,
        search: filters?.search,
        severity: filters?.severity,
        type: filters?.type
      },
      auth: true
    });
  }

  static async getErrorTypes(): Promise<GetErrorTypesResponse> {
    return APIService.inst.apiFetch<void, GetErrorTypesResponse>('GET', '/errors/types', {
      auth: true
    });
  }

  // Symbol API routes
  static async listSymbols(options?: ListSymbolsOptions): Promise<ListSymbolsResponse> {
    return APIService.inst.apiFetch<void, ListSymbolsResponse>('GET', '/symbols', {
      queryParams: {
          limit: options?.limit,
          next: options?.next,
          sort: options?.sort,
          sortDirection: options?.sortDirection,
          search: options?.search,
          ticker: options?.ticker,
          showInTicker: options?.showInTicker,
          stockExchangeId: options?.stockExchangeId,
          currentUpdateErrorCount: options?.currentUpdateErrorCount,
          currentUpdateErrorCountGreater: options?.currentUpdateErrorCountGreater,
      },
      auth: true
    });
  }

  static async getSymbol(symbolId: SymbolID): Promise<Symbol> {
    return APIService.inst.apiFetch<void, Symbol>('GET', `/symbols/${symbolId}`, {
      auth: true
    });
  }

  static async createSymbol(ticker: string, name: string | null, sector: string | null, showInTicker: boolean, stockExchangeId: StockExchangeID): Promise<Symbol> {
    return APIService.inst.apiFetch<{ticker: string, name: string | null, sector: string | null, showInTicker: boolean, stockExchangeId: StockExchangeID}, Symbol>('POST', '/symbols', {
      body: { ticker, name: name !== '' ? name : null, sector: sector !== '' ? sector : null, showInTicker, stockExchangeId },
      auth: true
    });
  }

  static async updateSymbol(symbolId: SymbolID, ticker: string, name: string | null, sector: string | null, showInTicker: boolean, stockExchangeId: StockExchangeID): Promise<Symbol> {
    return APIService.inst.apiFetch<{ticker: string, name: string | null, sector: string | null, showInTicker: boolean, stockExchangeId: StockExchangeID}, Symbol>('PATCH', `/symbols/${symbolId}`, {
      body: { ticker, name: name !== '' ? name : null, sector: sector !== '' ? sector : null, showInTicker, stockExchangeId },
      auth: true
    });
  }

  static async deleteSymbol(symbolId: SymbolID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/symbols/${symbolId}`, {
      auth: true
    });
  }

  static async verifySymbol(ticker: string, exchangeId: StockExchangeID): Promise<{ found: boolean; reason: string }> {
    return APIService.inst.apiFetch<{ ticker: string; exchangeId: StockExchangeID }, { found: boolean; reason: string }>('POST', '/symbols/verify', {
      body: { ticker, exchangeId },
      auth: true
    });
  }

  /**
   * Lists all unique sectors from existing symbols in the database.
   * Used for autocomplete functionality in sector input fields.
   *
   * @returns Promise resolving to an object containing an array of sector strings
   * @throws {Error} If the API request fails
   */
  static async listSectors(): Promise<{ sectors: string[] }> {
    return APIService.inst.apiFetch<void, { sectors: string[] }>('GET', '/sectors', {
      auth: true
    });
  }

  static async listSymbolHistory(symbolId: SymbolID, filters?: {
    next?: string;
    limit?: number;
    timestampAfter?: string;
    timestampBefore?: string;
    sort?: string;
    sortDirection?: 'ASC' | 'DESC';
  }): Promise<ListSymbolHistoryResponse> {
    return APIService.inst.apiFetch<void, ListSymbolHistoryResponse>('GET', `/symbols/${symbolId}/history`, {
      queryParams: {
        next: filters?.next,
        limit: filters?.limit,
        timestampAfter: filters?.timestampAfter,
        timestampBefore: filters?.timestampBefore,
        sort: filters?.sort,
        sortDirection: filters?.sortDirection
      },
      auth: true
    });
  }

  /**
   * Get downsampled graph data for a symbol.
   * Uses backend aggregation to return a fixed number of data points
   * regardless of the time range, making it efficient for long-term charts.
   */
  static async getSymbolGraph(symbolId: SymbolID, params: {
    start: string;
    end: string;
    points: number;
  }): Promise<GetSymbolGraphResponse> {
    return APIService.inst.apiFetch<void, GetSymbolGraphResponse>('GET', `/symbols/${symbolId}/graph`, {
      queryParams: {
        start: params.start,
        end: params.end,
        points: params.points
      },
      auth: true
    });
  }

  static async clearSymbolHistory(symbolId: SymbolID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/symbols/${symbolId}/history`, {
      auth: true
    });
  }

  static async updateSymbolData(symbolId: SymbolID): Promise<{ symbol: Symbol, changed: boolean }> {
    return APIService.inst.apiFetch<void, { symbol: Symbol, changed: boolean }>('POST', `/symbols/${symbolId}/update`, {
      auth: true
    });
  }

  // Core Recommendations
  static async listCoreRecommendations(options?: ListCoreRecommendationsOptions): Promise<ListCoreRecommendationsResponse> {
    return APIService.inst.apiFetch<void, ListCoreRecommendationsResponse>('GET', '/recommendations/core', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: options?.state,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        since: options?.since,
        until: options?.until
      },
      auth: true
    });
  }

  static async getCoreRecommendation(id: CoreRecommendationID): Promise<RecommendationCore> {
    return APIService.inst.apiFetch<void, RecommendationCore>('GET', `/recommendations/core/${id}`, {
      auth: true
    });
  }

  static async createCoreRecommendations(requests: CreateBatchCoreRecommendationRequest, dryRun: boolean): Promise<CreateCoreRecommendationsResponse> {
    return APIService.inst.apiFetch<CreateBatchCoreRecommendationRequest, CreateCoreRecommendationsResponse>('POST', '/recommendations/core', {
      body: requests,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async updateCoreRecommendation(id: CoreRecommendationID, request: UpdateCoreRecommendationRequest): Promise<RecommendationCore> {
    return APIService.inst.apiFetch<UpdateCoreRecommendationRequest, RecommendationCore>('PATCH', `/recommendations/core/${id}`, {
      body: request,
      auth: true
    });
  }

  static async deleteCoreRecommendation(id: CoreRecommendationID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/recommendations/core/${id}`, {
      auth: true
    });
  }

  // Core Recommendations Preview (PUBLIC - no auth required)
  static async listPreviewCoreRecommendations(options?: ListPreviewCoreRecommendationsOptions): Promise<ListPreviewCoreRecommendationsResponse> {
    return APIService.inst.apiFetch<void, ListPreviewCoreRecommendationsResponse>('GET', '/recommendations/core/preview', {
      queryParams: {
        state: options?.state,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next
      },
      auth: false
    });
  }

  // Core Recommendations Public (returns full models for closed/free trades without auth)
  static async listPublicCoreRecommendations(options?: ListCoreRecommendationsOptions): Promise<ListCoreRecommendationsResponse> {
    return APIService.inst.apiFetch<void, ListCoreRecommendationsResponse>('GET', '/recommendations/core', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: options?.state,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        since: options?.since,
        until: options?.until
      },
      auth: false
    });
  }

  // Earnings Recommendations
  static async listEarningsRecommendations(options?: ListEarningsRecommendationsOptions): Promise<ListEarningsRecommendationsResponse> {
    return APIService.inst.apiFetch<void, ListEarningsRecommendationsResponse>('GET', '/recommendations/earnings', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: options?.state,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        since: options?.since,
        until: options?.until
      },
      auth: true
    });
  }

  static async getEarningsRecommendation(id: EarningsRecommendationID): Promise<RecommendationEarnings> {
    return APIService.inst.apiFetch<void, RecommendationEarnings>('GET', `/recommendations/earnings/${id}`, {
      auth: true
    });
  }

  static async createEarningsRecommendations(requests: CreateBatchEarningsRecommendationRequest, dryRun: boolean): Promise<CreateEarningsRecommendationsResponse> {
    return APIService.inst.apiFetch<CreateBatchEarningsRecommendationRequest, CreateEarningsRecommendationsResponse>('POST', '/recommendations/earnings', {
      body: requests,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async updateEarningsRecommendation(id: EarningsRecommendationID, request: UpdateEarningsRecommendationRequest): Promise<RecommendationEarnings> {
    return APIService.inst.apiFetch<UpdateEarningsRecommendationRequest, RecommendationEarnings>('PATCH', `/recommendations/earnings/${id}`, {
      body: request,
      auth: true
    });
  }

  static async deleteEarningsRecommendation(id: EarningsRecommendationID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/recommendations/earnings/${id}`, {
      auth: true
    });
  }

  // Earnings Recommendations Preview (PUBLIC - no auth required)
  static async listPreviewEarningsRecommendations(options?: ListPreviewEarningsRecommendationsOptions): Promise<ListPreviewEarningsRecommendationsResponse> {
    return APIService.inst.apiFetch<void, ListPreviewEarningsRecommendationsResponse>('GET', '/recommendations/earnings/preview', {
      queryParams: {
        state: options?.state,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next
      },
      auth: false
    });
  }

  // Earnings Recommendations Public (returns full models for closed/free trades without auth)
  static async listPublicEarningsRecommendations(options?: ListEarningsRecommendationsOptions): Promise<ListEarningsRecommendationsResponse> {
    return APIService.inst.apiFetch<void, ListEarningsRecommendationsResponse>('GET', '/recommendations/earnings', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: options?.state,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        since: options?.since,
        until: options?.until
      },
      auth: false
    });
  }

  // Swing Trades
  static async listSwingTrades(options?: ListSwingTradesOptions): Promise<ListSwingTradesResponse> {
    return APIService.inst.apiFetch<void, ListSwingTradesResponse>('GET', '/recommendations/swing-trades', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: toCommaSeparated(options?.state),
        type: options?.type,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        excludeFinished: options?.excludeFinished,
        since: options?.since,
        until: options?.until
      },
      auth: true
    });
  }

  static async getSwingTrade(id: SwingTradeID): Promise<RecommendationSwingtrade> {
    return APIService.inst.apiFetch<void, RecommendationSwingtrade>('GET', `/recommendations/swing-trades/${id}`, {
      auth: true
    });
  }

  static async createSwingTrades(requests: CreateBatchSwingTradeRequest, dryRun: boolean): Promise<CreateSwingTradesResponse> {
    return APIService.inst.apiFetch<CreateBatchSwingTradeRequest, CreateSwingTradesResponse>('POST', '/recommendations/swing-trades', {
      body: requests,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async updateSwingTrade(id: SwingTradeID, request: UpdateSwingTradeRequest): Promise<RecommendationSwingtrade> {
    return APIService.inst.apiFetch<UpdateSwingTradeRequest, RecommendationSwingtrade>('PATCH', `/recommendations/swing-trades/${id}`, {
      body: request,
      auth: true
    });
  }

  static async finishSwingTrade(id: SwingTradeID, request: FinishSwingTradeRequest, dryRun: boolean): Promise<{ recommendation: RecommendationSwingtrade, stats: RecommendationCreateStats }> {
    return APIService.inst.apiFetch<FinishSwingTradeRequest, { recommendation: RecommendationSwingtrade, stats: RecommendationCreateStats }>('POST', `/recommendations/swing-trades/${id}/finish`, {
      body: request,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async deleteSwingTrade(id: SwingTradeID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/recommendations/swing-trades/${id}`, {
      auth: true
    });
  }

  // ===== Recommendation Favorites =====

  static async listFavorites(options?: ListFavoritesOptions): Promise<ListFavoritesResponse> {
    return APIService.inst.apiFetch<void, ListFavoritesResponse>('GET', '/favorites', {
      queryParams: {
        type: toCommaSeparated(options?.type),
        active: options?.active,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next
      },
      auth: true
    });
  }

  static async createFavorite(request: CreateFavoriteRequest): Promise<RecommendationFavorite> {
    return APIService.inst.apiFetch<CreateFavoriteRequest, RecommendationFavorite>('POST', '/favorites', {
      body: request,
      auth: true
    });
  }

  static async updateFavorite(id: RecommendationFavoriteID, request: UpdateFavoriteRequest): Promise<RecommendationFavorite> {
    return APIService.inst.apiFetch<UpdateFavoriteRequest, RecommendationFavorite>('PATCH', `/favorites/${id}`, {
      body: request,
      auth: true
    });
  }

  static async deleteFavorite(id: RecommendationFavoriteID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/favorites/${id}`, {
      auth: true
    });
  }

  // Swing Trade Adjustments
  static async listSwingTradeAdjustements(id: SwingTradeID): Promise<ListSwingTradeAdjustementsResponse> {
    return APIService.inst.apiFetch<void, ListSwingTradeAdjustementsResponse>('GET', '/recommendations/swing-trades/:id/adjustements', {
      urlParams: { id },
      auth: true
    });
  }

  static async getSwingTradeAdjustement(id: SwingTradeID, aid: SwingTradeAdjustementID): Promise<SwingTradeAdjustement> {
    return APIService.inst.apiFetch<void, SwingTradeAdjustement>('GET', '/recommendations/swing-trades/:id/adjustements/:aid', {
      urlParams: { id, aid },
      auth: true
    });
  }

  static async addSwingTradeAdjustement(id: SwingTradeID, request: AddSwingTradeAdjustementRequest, dryRun: boolean): Promise<{ recommendation: RecommendationSwingtrade, stats: RecommendationCreateStats }> {
    return APIService.inst.apiFetch<AddSwingTradeAdjustementRequest, { recommendation: RecommendationSwingtrade, stats: RecommendationCreateStats }>('POST', '/recommendations/swing-trades/:id/adjustements', {
      urlParams: { id },
      body: request,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async updateSwingTradeAdjustement(id: SwingTradeID, aid: SwingTradeAdjustementID, request: UpdateSwingTradeAdjustementRequest): Promise<RecommendationSwingtrade> {
    return APIService.inst.apiFetch<UpdateSwingTradeAdjustementRequest, RecommendationSwingtrade>('PATCH', '/recommendations/swing-trades/:id/adjustements/:aid', {
      urlParams: { id, aid },
      body: request,
      auth: true
    });
  }

  static async deleteSwingTradeAdjustement(id: SwingTradeID, aid: SwingTradeAdjustementID): Promise<RecommendationSwingtrade> {
    return APIService.inst.apiFetch<void, RecommendationSwingtrade>('DELETE', '/recommendations/swing-trades/:id/adjustements/:aid', {
      urlParams: { id, aid },
      auth: true
    });
  }

  // Swing Trades Preview (PUBLIC - no auth required)
  static async listPreviewSwingTrades(options?: ListPreviewSwingTradesOptions): Promise<ListPreviewSwingTradesResponse> {
    return APIService.inst.apiFetch<void, ListPreviewSwingTradesResponse>('GET', '/recommendations/swing-trades/preview', {
      queryParams: {
        state: toCommaSeparated(options?.state),
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next
      },
      auth: false
    });
  }

  // Swing Trades Public (returns full models for closed/free trades without auth)
  static async listPublicSwingTrades(options?: ListSwingTradesOptions): Promise<ListSwingTradesResponse> {
    return APIService.inst.apiFetch<void, ListSwingTradesResponse>('GET', '/recommendations/swing-trades', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: toCommaSeparated(options?.state),
        type: options?.type,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        excludeFinished: options?.excludeFinished,
        since: options?.since,
        until: options?.until
      },
      auth: false
    });
  }

  // Long-Term Recommendations
  static async listLongTermTrades(options?: ListLongTermTradesOptions): Promise<ListLongTermTradesResponse> {
    return APIService.inst.apiFetch<void, ListLongTermTradesResponse>('GET', '/recommendations/long-term', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: toCommaSeparated(options?.state),
        type: options?.type,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        excludeFinished: options?.excludeFinished,
        since: options?.since,
        until: options?.until
      },
      auth: true
    });
  }

  static async getLongTermTrade(id: LongTermRecommendationID): Promise<LongTermRecommendation> {
    return APIService.inst.apiFetch<void, LongTermRecommendation>('GET', `/recommendations/long-term/${id}`, {
      auth: true
    });
  }

  static async createLongTermTrades(requests: CreateBatchLongTermTradeRequest, dryRun: boolean): Promise<CreateLongTermTradesResponse> {
    return APIService.inst.apiFetch<CreateBatchLongTermTradeRequest, CreateLongTermTradesResponse>('POST', '/recommendations/long-term', {
      body: requests,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async updateLongTermTrade(id: LongTermRecommendationID, request: UpdateLongTermTradeRequest): Promise<LongTermRecommendation> {
    return APIService.inst.apiFetch<UpdateLongTermTradeRequest, LongTermRecommendation>('PATCH', `/recommendations/long-term/${id}`, {
      body: request,
      auth: true
    });
  }

  static async finishLongTermTrade(id: LongTermRecommendationID, request: FinishLongTermTradeRequest, dryRun: boolean): Promise<{ recommendation: LongTermRecommendation, stats: RecommendationCreateStats }> {
    return APIService.inst.apiFetch<FinishLongTermTradeRequest, { recommendation: LongTermRecommendation, stats: RecommendationCreateStats }>('POST', `/recommendations/long-term/${id}/finish`, {
      body: request,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async deleteLongTermTrade(id: LongTermRecommendationID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', `/recommendations/long-term/${id}`, {
      auth: true
    });
  }

  // Long-Term Trade Adjustments
  static async listLongTermTradeAdjustements(id: LongTermRecommendationID): Promise<ListLongTermAdjustementsResponse> {
    return APIService.inst.apiFetch<void, ListLongTermAdjustementsResponse>('GET', '/recommendations/long-term/:id/adjustements', {
      urlParams: { id },
      auth: true
    });
  }

  static async getLongTermTradeAdjustement(id: LongTermRecommendationID, aid: LongTermAdjustementID): Promise<LongTermAdjustement> {
    return APIService.inst.apiFetch<void, LongTermAdjustement>('GET', '/recommendations/long-term/:id/adjustements/:aid', {
      urlParams: { id, aid },
      auth: true
    });
  }

  static async addLongTermTradeAdjustement(id: LongTermRecommendationID, request: AddLongTermAdjustementRequest, dryRun: boolean): Promise<{ recommendation: LongTermRecommendation, stats: RecommendationCreateStats }> {
    return APIService.inst.apiFetch<AddLongTermAdjustementRequest, { recommendation: LongTermRecommendation, stats: RecommendationCreateStats }>('POST', '/recommendations/long-term/:id/adjustements', {
      urlParams: { id },
      body: request,
      queryParams: { dryRun: dryRun },
      auth: true
    });
  }

  static async updateLongTermTradeAdjustement(id: LongTermRecommendationID, aid: LongTermAdjustementID, request: UpdateLongTermAdjustementRequest): Promise<LongTermRecommendation> {
    return APIService.inst.apiFetch<UpdateLongTermAdjustementRequest, LongTermRecommendation>('PATCH', '/recommendations/long-term/:id/adjustements/:aid', {
      urlParams: { id, aid },
      body: request,
      auth: true
    });
  }

  static async deleteLongTermTradeAdjustement(id: LongTermRecommendationID, aid: LongTermAdjustementID): Promise<LongTermRecommendation> {
    return APIService.inst.apiFetch<void, LongTermRecommendation>('DELETE', '/recommendations/long-term/:id/adjustements/:aid', {
      urlParams: { id, aid },
      auth: true
    });
  }

  // Long-Term Trades Preview (PUBLIC - no auth required)
  static async listPreviewLongTermTrades(options?: ListPreviewLongTermTradesOptions): Promise<ListPreviewLongTermTradesResponse> {
    return APIService.inst.apiFetch<void, ListPreviewLongTermTradesResponse>('GET', '/recommendations/long-term/preview', {
      queryParams: {
        state: toCommaSeparated(options?.state),
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next
      },
      auth: false
    });
  }

  // Long-Term Trades Public (returns full models for closed/free trades without auth)
  static async listPublicLongTermTrades(options?: ListLongTermTradesOptions): Promise<ListLongTermTradesResponse> {
    return APIService.inst.apiFetch<void, ListLongTermTradesResponse>('GET', '/recommendations/long-term', {
      queryParams: {
        symbolTicker: options?.ticker,
        state: toCommaSeparated(options?.state),
        type: options?.type,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        limit: options?.limit,
        next: options?.next,
        excludeFinished: options?.excludeFinished,
        since: options?.since,
        until: options?.until
      },
      auth: false
    });
  }

  // Day Trading Sessions Public (returns closed sessions without auth)
  static async listPublicDayTradingSessions(options?: ListDayTradingSessionsOptions): Promise<ListDayTradingSessionsResponse> {
    return APIService.inst.apiFetch<void, ListDayTradingSessionsResponse>('GET', '/day-trading/sessions', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        open: options?.open,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        since: options?.since,
        until: options?.until,
      },
      auth: false
    });
  }

  // Recalculate all recommendations
  static async recalcAllRecommendations(): Promise<RecalcAllRecommendationsResponse> {
    return APIService.inst.apiFetch<void, RecalcAllRecommendationsResponse>('POST', '/recommendations/recalc-all', {
      auth: true
    });
  }

  // Recalculate all users
  static async recalcAllUsers(): Promise<{ matched: number, changed: number }> {
    return APIService.inst.apiFetch<void, { matched: number, changed: number }>('POST', '/users/recalc-all', {
      auth: true
    });
  }

  // Course API routes
  static async listCourses(options?: ListCoursesOptions): Promise<ListCoursesResponse> {
    return APIService.inst.apiFetch<void, ListCoursesResponse>('GET', '/courses', {
      queryParams: {
        published: options?.published,
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDir: options?.sortDir,
        search: options?.search,
        free: options?.free,
        hasMailSequence: options?.hasMailSequence,
        onlyCommunityMembers: options?.onlyCommunityMembers,
        excludeCommunityMembers: options?.excludeCommunityMembers,
      },
      auth: true
    });
  }

  static async getCourse(courseId: CourseID): Promise<Course> {
    return APIService.inst.apiFetch<void, Course>('GET', '/courses/:courseId', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async createCourse(request: CreateCourseRequest): Promise<Course> {
    return APIService.inst.apiFetch<CreateCourseRequest, Course>('POST', '/courses', {
      body: request,
      auth: true
    });
  }

  static async updateCourse(courseId: CourseID, request: UpdateCourseRequest): Promise<Course> {
    return APIService.inst.apiFetch<UpdateCourseRequest, Course>('PATCH', '/courses/:courseId', {
      urlParams: { courseId },
      body: request,
      auth: true
    });
  }

  static async deleteCourse(courseId: CourseID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/courses/:courseId', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async publishCourse(courseId: CourseID): Promise<Course> {
    return APIService.inst.apiFetch<void, Course>('POST', '/courses/:courseId/publish', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async unpublishCourse(courseId: CourseID): Promise<Course> {
    return APIService.inst.apiFetch<void, Course>('POST', '/courses/:courseId/unpublish', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async listCourseUsers(
    courseId: CourseID,
    options?: {
      search?: string;
    }
  ): Promise<ListCourseUsersResponse> {
    return APIService.inst.apiFetch<void, ListCourseUsersResponse>('GET', '/courses/:courseId/users', {
      urlParams: { courseId },
      queryParams: {
        search: options?.search
      },
      auth: true
    });
  }

  static async getCourseMailSequence(courseId: CourseID): Promise<CourseMailSequence> {
    return APIService.inst.apiFetch<void, CourseMailSequence>('GET', '/courses/:courseId/mail-sequence', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async updateCourseMailSequence(courseId: CourseID, request: UpdateCourseMailSequenceRequest): Promise<CourseMailSequence> {
    return APIService.inst.apiFetch<UpdateCourseMailSequenceRequest, CourseMailSequence>('PATCH', '/courses/:courseId/mail-sequence', {
      urlParams: { courseId },
      body: request,
      auth: true
    });
  }

  // Module API routes
  static async listModules(courseId: CourseID, options?: ListModulesOptions): Promise<ListModulesResponse> {
    return APIService.inst.apiFetch<void, ListModulesResponse>('GET', '/courses/:courseId/modules', {
      urlParams: { courseId },
      queryParams: {
        published: options?.published,
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDir: options?.sortDir
      },
      auth: true
    });
  }

  static async getModule(moduleId: ModuleID): Promise<Module> {
    return APIService.inst.apiFetch<void, Module>('GET', '/modules/:moduleId', {
      urlParams: { moduleId },
      auth: true
    });
  }

  static async createModule(courseId: CourseID, request: CreateModuleRequest): Promise<Module> {
    return APIService.inst.apiFetch<CreateModuleRequest, Module>('POST', '/courses/:courseId/modules', {
      urlParams: { courseId },
      body: request,
      auth: true
    });
  }

  static async updateModule(moduleId: ModuleID, request: UpdateModuleRequest): Promise<Module> {
    return APIService.inst.apiFetch<UpdateModuleRequest, Module>('PATCH', '/modules/:moduleId', {
      urlParams: { moduleId },
      body: request,
      auth: true
    });
  }

  static async deleteModule(moduleId: ModuleID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/modules/:moduleId', {
      urlParams: { moduleId },
      auth: true
    });
  }

  static async publishModule(moduleId: ModuleID): Promise<Module> {
    return APIService.inst.apiFetch<void, Module>('POST', '/modules/:moduleId/publish', {
      urlParams: { moduleId },
      auth: true
    });
  }

  static async unpublishModule(moduleId: ModuleID): Promise<Module> {
    return APIService.inst.apiFetch<void, Module>('POST', '/modules/:moduleId/unpublish', {
      urlParams: { moduleId },
      auth: true
    });
  }

  // List all modules across all courses (admin endpoint)
  static async listAllModules(options?: ListAllModulesOptions): Promise<ListAllModulesResponse> {
    return APIService.inst.apiFetch<void, ListAllModulesResponse>('GET', '/modules', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDir: options?.sortDir,
        search: options?.search,
        published: options?.published,
        courseId: options?.courseId,
      },
      auth: true
    });
  }

  // Video API routes
  static async listVideos(moduleId: ModuleID, options?: ListVideosOptions): Promise<ListVideosResponse> {
    return APIService.inst.apiFetch<void, ListVideosResponse>('GET', '/modules/:moduleId/videos', {
      urlParams: { moduleId },
      queryParams: {
        published: options?.published,
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDir: options?.sortDir,
        includeHiddenByModuleLimit: options?.includeHiddenByModuleLimit
      },
      auth: true
    });
  }

  // List all videos across all modules (admin endpoint)
  static async listAllVideos(options?: ListAllVideosOptions): Promise<ListAllVideosResponse> {
    return APIService.inst.apiFetch<void, ListAllVideosResponse>('GET', '/videos', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDir: options?.sortDir,
        search: options?.search,
        courseId: options?.courseId,
        moduleId: options?.moduleId,
        providesKnowledge: options?.providesKnowledge,
        includeHiddenByModuleLimit: options?.includeHiddenByModuleLimit,
      },
      auth: true
    });
  }

  static async getVideo(videoId: VideoID): Promise<Video> {
    return APIService.inst.apiFetch<void, Video>('GET', '/videos/:videoId', {
      urlParams: { videoId },
      auth: true
    });
  }

  static async createVideo(moduleId: ModuleID, request: CreateVideoRequest): Promise<Video> {
    return APIService.inst.apiFetch<CreateVideoRequest, Video>('POST', '/modules/:moduleId/videos', {
      urlParams: { moduleId },
      body: request,
      auth: true
    });
  }

  static async updateVideo(videoId: VideoID, request: UpdateVideoRequest): Promise<Video> {
    return APIService.inst.apiFetch<UpdateVideoRequest, Video>('PATCH', '/videos/:videoId', {
      urlParams: { videoId },
      body: request,
      auth: true
    });
  }

  static async deleteVideo(videoId: VideoID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/videos/:videoId', {
      urlParams: { videoId },
      auth: true
    });
  }

  static async publishVideo(videoId: VideoID, sendDiscord: boolean = false, sendTelegram: boolean = false, sendMail: boolean = false, sendPush: boolean = false): Promise<Video> {
    return APIService.inst.apiFetch<{ sendDiscord: boolean; sendTelegram: boolean; sendMail: boolean; sendPush: boolean }, Video>('POST', '/videos/:videoId/publish', {
      urlParams: { videoId },
      body: { sendDiscord, sendTelegram, sendMail, sendPush },
      auth: true
    });
  }

  static async unpublishVideo(videoId: VideoID): Promise<Video> {
    return APIService.inst.apiFetch<void, Video>('POST', '/videos/:videoId/unpublish', {
      urlParams: { videoId },
      auth: true
    });
  }

  static async moveVideo(videoId: VideoID, moduleId: ModuleID): Promise<Video> {
    return APIService.inst.apiFetch<{ moduleId: ModuleID }, Video>('POST', '/videos/:videoId/move', {
      urlParams: { videoId },
      body: { moduleId },
      auth: true
    });
  }

  // User Video Progress API routes
  static async updateVideoProgress(videoId: VideoID, request: UpdateVideoProgressRequest): Promise<UpdateVideoProgressResponse> {
    return APIService.inst.apiFetch<UpdateVideoProgressRequest, UpdateVideoProgressResponse>('PUT', '/users/self/videos/:videoId/progress', {
      urlParams: { videoId },
      body: request,
      auth: true
    });
  }

  static async getCourseProgress(courseId: CourseID): Promise<GetCourseProgressResponse> {
    return APIService.inst.apiFetch<void, GetCourseProgressResponse>('GET', '/users/self/courses/:courseId/progress', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async startCourse(courseId: CourseID): Promise<UserCourseProgressResponse> {
    return APIService.inst.apiFetch<void, UserCourseProgressResponse>('POST', '/users/self/courses/:courseId/progress/start', {
      urlParams: { courseId },
      auth: true
    });
  }

  static async getModuleProgress(moduleId: ModuleID): Promise<GetModuleProgressResponse> {
    return APIService.inst.apiFetch<void, GetModuleProgressResponse>('GET', '/users/self/modules/:moduleId/progress', {
      urlParams: { moduleId },
      auth: true
    });
  }

  static async startModule(moduleId: ModuleID): Promise<UserModuleProgressResponse> {
    return APIService.inst.apiFetch<void, UserModuleProgressResponse>('POST', '/users/self/modules/:moduleId/progress/start', {
      urlParams: { moduleId },
      auth: true
    });
  }

  // Thumbnail upload API routes - send raw binary data
  static async uploadCourseThumbnail(courseId: CourseID, imageFile: File): Promise<void> {
    const url = APIService.inst.buildUrl('/courses/:courseId/thumbnail', { courseId });

    const token = APIService.inst.getToken();
    const headers: Record<string, string> = {
      'Content-Type': imageFile.type,
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: imageFile
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload thumbnail: ${errorText}`);
    }
  }

  static async uploadModuleThumbnail(moduleId: ModuleID, imageFile: File): Promise<void> {
    const url = APIService.inst.buildUrl('/modules/:moduleId/thumbnail', { moduleId });

    const token = APIService.inst.getToken();
    const headers: Record<string, string> = {
      'Content-Type': imageFile.type,
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: imageFile
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload thumbnail: ${errorText}`);
    }
  }

  static async uploadVideoThumbnail(videoId: VideoID, imageFile: File): Promise<void> {
    const url = APIService.inst.buildUrl('/videos/:videoId/thumbnail', { videoId });

    const token = APIService.inst.getToken();
    const headers: Record<string, string> = {
      'Content-Type': imageFile.type,
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: imageFile
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload thumbnail: ${errorText}`);
    }
  }

  // Video Chat API routes
  static async getVideoChat(videoId: VideoID): Promise<ChatSession> {
    return APIService.inst.apiFetch<void, ChatSession>('GET', '/videos/:videoId/chat', {
      urlParams: { videoId },
      auth: true
    });
  }

  // Video Knowledge API routes
  static async getVideoKnowledge(videoId: VideoID): Promise<VideoKnowledge> {
    return APIService.inst.apiFetch<void, VideoKnowledge>('GET', '/videos/:videoId/knowledge', {
      urlParams: { videoId },
      auth: true
    });
  }

  static async createVideoKnowledge(videoId: VideoID, request: Partial<VideoKnowledge>): Promise<VideoKnowledge> {
    return APIService.inst.apiFetch<Partial<VideoKnowledge>, VideoKnowledge>('POST', '/videos/:videoId/knowledge', {
      urlParams: { videoId },
      body: request,
      auth: true
    });
  }

  static async deleteVideoKnowledge(videoId: VideoID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/videos/:videoId/knowledge', {
      urlParams: { videoId },
      auth: true
    });
  }

  static async uploadVideo(
    videoId: VideoID,
    file: File,
    callback: (p: number) => void,
    createKnowledge: boolean,
    autoPublish: boolean,
    autoPublishSendDiscord: boolean,
    autoPublishSendMail: boolean,
    autoPublishSendTelegram: boolean
  ): Promise<Video> {
      // Create XMLHttpRequest to track upload progress
      const xhr = new XMLHttpRequest();

      // Monitor upload progress
      xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              callback(progress);
          }
      });

      // Handle upload completion
      const uploadPromise = new Promise<Video>((resolve, reject) => {
          xhr.onload = () => {
              if (xhr.status === 200) {
                  try {
                      const response = JSON.parse(xhr.responseText);
                      console.log('uploadVideo API response:', response);

                      // Handle both wrapped and direct video responses
                      const video = response.video || response;

                      if (!video || !video.id) {
                          console.error('uploadVideo: Invalid video object received:', video);
                          reject(new Error('Invalid video object in response'));
                          return;
                      }

                      console.log('uploadVideo: Resolved with video:', video);
                      resolve(video);
                  } catch (parseError) {
                      console.error('uploadVideo: JSON parse error:', parseError);
                      reject(new Error('Invalid response format'));
                  }
              } else {
                  try {
                      const errorResponse = JSON.parse(xhr.responseText);
                      reject(new Error(errorResponse.message || `Upload failed with status ${xhr.status}`));
                  } catch {
                      reject(new Error(`Upload failed with status ${xhr.status}`));
                  }
              }
          };

          xhr.onerror = () => reject(new Error('Upload failed due to network error'));
          xhr.onabort = () => reject(new Error('Upload was aborted'));
      });

      // Build URL with query parameters
      const queryParams = new URLSearchParams({
          createKnowledge: String(createKnowledge),
          autoPublish: String(autoPublish),
          autoPublishSendDiscord: String(autoPublishSendDiscord),
          autoPublishSendMail: String(autoPublishSendMail),
          autoPublishSendTelegram: String(autoPublishSendTelegram)
      });
      const url = import.meta.env.VITE_API_URL + `/videos/${videoId}/upload?${queryParams.toString()}`;

      xhr.open('POST', url, true);
      //xhr.setRequestHeader('Content-Type', file.type);
      xhr.setRequestHeader('Authorization', `Bearer ${APIService.inst.getToken()}`);

      xhr.send(file);

      return await uploadPromise;
  }

  static async resetVideoProcessing(videoId: VideoID): Promise<Video> {
    return APIService.inst.apiFetch<void, Video>('POST', '/videos/:videoId/reset-processing', {
      urlParams: { videoId },
      auth: true
    });
  }

  // Video Attachment API routes
  static async listVideoAttachments(videoId: VideoID, options?: ListVideoAttachmentsOptions): Promise<ListVideoAttachmentsResponse> {
    return APIService.inst.apiFetch<void, ListVideoAttachmentsResponse>('GET', '/videos/:videoId/attachments', {
      urlParams: { videoId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        sort: options?.sort,
        sortDirection: options?.sortDirection
      },
      auth: true
    });
  }

  static async getVideoAttachment(attachmentId: VideoAttachmentID): Promise<VideoAttachment> {
    return APIService.inst.apiFetch<void, VideoAttachment>('GET', '/video-attachments/:attachmentId', {
      urlParams: { attachmentId },
      auth: true
    });
  }

  static async uploadVideoAttachment(videoId: VideoID, file: File, filename?: string, description?: string, order?: number): Promise<VideoAttachment> {
    const url = APIService.inst.buildUrl('/videos/:videoId/attachments', { videoId }, {
      filename:    filename,
      description: description,
      order:       order,
    });

    const token = APIService.inst.getToken();
    const headers: Record<string, string> = {
        'Content-Type': file.type,
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: file
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload thumbnail: ${errorText}`);
    }

    return await response.json();
  }

  static async updateVideoAttachment(attachmentId: VideoAttachmentID, request: UpdateVideoAttachmentRequest): Promise<VideoAttachment> {
    return APIService.inst.apiFetch<UpdateVideoAttachmentRequest, VideoAttachment>('PATCH', '/video-attachments/:attachmentId', {
      urlParams: { attachmentId },
      body: request,
      auth: true
    });
  }

  static async deleteVideoAttachment(attachmentId: VideoAttachmentID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/video-attachments/:attachmentId', {
      urlParams: { attachmentId },
      auth: true
    });
  }

  static async downloadVideoAttachment(attachmentId: VideoAttachmentID): Promise<Blob> {
    const url = APIService.inst.buildUrl('/video-attachments/:attachmentId/download', { attachmentId });

    const token = APIService.inst.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download attachment: ${errorText}`);
    }

    return await response.blob();
  }

  // Mail API routes
  static async listMails(options?: ListMailsOptions): Promise<ListMailsResponse> {
    return APIService.inst.apiFetch<void, ListMailsResponse>('GET', '/mails', {
      queryParams: {
        status: options?.status,
        receiver: options?.receiver,
        template: options?.template,
        userID: options?.userID,
        since: options?.since,
        until: options?.until,
        next: options?.next,
        limit: options?.limit,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        minPriority: options?.minPriority,
        maxRetries: options?.maxRetries
      },
      auth: true
    });
  }

  static async getMail(mailId: MailID): Promise<Mail> {
    return APIService.inst.apiFetch<void, Mail>('GET', '/mails/:mid', {
      urlParams: { mid: mailId },
      auth: true
    });
  }

  static async setMailToFailed(mailId: MailID, reason: string): Promise<Mail> {
    return APIService.inst.apiFetch<SetMailToFailedRequest, Mail>('POST', '/mails/:mid/failed', {
      urlParams: { mid: mailId },
      body: { reason },
      auth: true
    });
  }

  static async resetMailForRetry(mailId: MailID): Promise<Mail> {
    return APIService.inst.apiFetch<void, Mail>('POST', '/mails/:mid/retry', {
      urlParams: { mid: mailId },
      auth: true
    });
  }

  static async getMailQueueStats(): Promise<MailQueueStats> {
    return APIService.inst.apiFetch<void, MailQueueStats>('GET', '/mails/stats', {
      auth: true
    });
  }

  static async sendTestMail(receiverEmail: string): Promise<Mail> {
    return APIService.inst.apiFetch<SendTestMailRequest, Mail>('POST', '/mails/test', {
      body: {
        receiverEmail: receiverEmail,
      },
      auth: true
    });
  }

  // Discord API routes
  static async listDiscordMessages(options?: ListDiscordMessagesOptions): Promise<ListDiscordMessagesResponse> {
    return APIService.inst.apiFetch<void, ListDiscordMessagesResponse>('GET', '/discord-messages', {
      queryParams: {
        status: options?.status,
        channelId: options?.channelId,
        next: options?.next,
        limit: options?.limit
      },
      auth: true
    });
  }

  static async getDiscordMessage(discordMessageId: DiscordMessageID): Promise<DiscordMessage> {
    return APIService.inst.apiFetch<void, DiscordMessage>('GET', '/discord-messages/:dmid', {
      urlParams: { dmid: discordMessageId },
      auth: true
    });
  }

  static async sendDiscordMessage(request: SendDiscordMessageRequest): Promise<DiscordMessage> {
    return APIService.inst.apiFetch<SendDiscordMessageRequest, DiscordMessage>('POST', '/discord-messages', {
      body: request,
      auth: true
    });
  }

  // Telegram API routes
  static async listTelegramMessages(options?: ListTelegramMessagesOptions): Promise<ListTelegramMessagesResponse> {
    return APIService.inst.apiFetch<void, ListTelegramMessagesResponse>('GET', '/telegram-messages', {
      queryParams: {
        status: options?.status,
        chatId: options?.chatId,
        next: options?.next,
        limit: options?.limit
      },
      auth: true
    });
  }

  static async getTelegramMessage(telegramMessageId: TelegramMessageID): Promise<TelegramMessage> {
    return APIService.inst.apiFetch<void, TelegramMessage>('GET', '/telegram-messages/:tmid', {
      urlParams: { tmid: telegramMessageId },
      auth: true
    });
  }

  static async sendTelegramMessage(request: SendTelegramMessageRequest): Promise<TelegramMessage> {
    return APIService.inst.apiFetch<SendTelegramMessageRequest, TelegramMessage>('POST', '/telegram-messages', {
      body: request,
      auth: true
    });
  }

  // SCN API routes
  static async listSCNMessages(options?: ListSCNMessagesOptions): Promise<ListSCNMessagesResponse> {
    return APIService.inst.apiFetch<void, ListSCNMessagesResponse>('GET', '/scn-messages', {
      queryParams: {
        status: options?.status,
        uid: options?.uid,
        channel: options?.channel,
        next: options?.next,
        limit: options?.limit
      },
      auth: true
    });
  }

  static async getSCNMessage(scnMessageId: SCNMessageID): Promise<SCNMessage> {
    return APIService.inst.apiFetch<void, SCNMessage>('GET', '/scn-messages/:smid', {
      urlParams: { smid: scnMessageId },
      auth: true
    });
  }

  static async sendSCNMessage(request: SendSCNMessageRequest): Promise<SCNMessage> {
    return APIService.inst.apiFetch<SendSCNMessageRequest, SCNMessage>('POST', '/scn-messages', {
      body: request,
      auth: true
    });
  }

  // Push Notification API routes
  static async listPushNotifications(options?: ListPushNotificationsOptions): Promise<ListPushNotificationsResponse> {
    return APIService.inst.apiFetch<void, ListPushNotificationsResponse>('GET', '/push-notifications', {
      queryParams: {
        status: options?.status,
        userID: options?.userID,
        next: options?.next,
        limit: options?.limit
      },
      auth: true
    });
  }

  static async getPushNotification(pushNotificationId: PushNotificationID): Promise<PushNotification> {
    return APIService.inst.apiFetch<void, PushNotification>('GET', '/push-notifications/:pnid', {
      urlParams: { pnid: pushNotificationId },
      auth: true
    });
  }

  static async sendPushNotification(request: SendPushNotificationRequest): Promise<PushNotification> {
    return APIService.inst.apiFetch<SendPushNotificationRequest, PushNotification>('POST', '/push-notifications', {
      body: request,
      auth: true
    });
  }

  static async resolveShortcode(code: string): Promise<{ redirectUrl: string }> {
    return APIService.inst.apiFetch<void, { redirectUrl: string }>('GET', '/shortcodes/:code', {
      urlParams: { code },
      auth: false
    });
  }

  // ============================================
  // Dashboard
  // ============================================

  static async getDashboard(): Promise<GetDashboardResponse> {
    return APIService.inst.apiFetch<void, GetDashboardResponse>('GET', '/dashboard', {
      auth: true
    });
  }

  // ============================================
  // Statistics
  // ============================================

  static async getStatistics(): Promise<RecommendationStatsOverview> {
    return APIService.inst.apiFetch<void, RecommendationStatsOverview>('GET', '/statistics', {
      auth: true
    });
  }

  // ============================================
  // Stocks Status Light
  // ============================================

  static async getLatestStocksStatusLight(): Promise<StocksStatusLight> {
    return APIService.inst.apiFetch<void, StocksStatusLight>('GET', '/stocks-status-light', {
      auth: false
    });
  }

  static async listStocksStatusLights(options?: ListStocksStatusLightsOptions): Promise<ListStocksStatusLightsResponse> {
    return APIService.inst.apiFetch<void, ListStocksStatusLightsResponse>('GET', '/stocks-status-lights', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        color: options?.color,
        sort: options?.sort,
        sortDirection: options?.sortDirection
      },
      auth: true
    });
  }

  static async createStocksStatusLight(request: CreateStocksStatusLightRequest): Promise<StocksStatusLight> {
    return APIService.inst.apiFetch<CreateStocksStatusLightRequest, StocksStatusLight>('POST', '/stocks-status-light', {
      body: request,
      auth: true
    });
  }

  // ============================================
  // Subscriptions
  // ============================================

  // Create a subscription (returns checkout URL)
  static async createSubscription(userId: UserID, request: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
    return APIService.inst.apiFetch<CreateSubscriptionRequest, CreateSubscriptionResponse>('POST', '/users/:uid/subscriptions', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  // Get user subscriptions
  static async getUserSubscriptions(userId: UserID): Promise<GetUserSubscriptionsResponse> {
    return APIService.inst.apiFetch<void, GetUserSubscriptionsResponse>('GET', '/users/:uid/subscriptions', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  // Cancel a subscription
  static async cancelSubscription(userId: UserID, subscriptionId: SubscriptionID, options?: { cancelImmediately?: boolean }): Promise<CancelSubscriptionResponse> {
    return APIService.inst.apiFetch<void, CancelSubscriptionResponse>('DELETE', '/users/:uid/subscriptions/:sid', {
      urlParams: { uid: userId, sid: subscriptionId },
      queryParams: {
        cancelImmediately: options?.cancelImmediately
      },
      auth: true
    });
  }

  // Get subscription events (admin only)
  static async getSubscriptionEvents(userId: UserID, subscriptionId: SubscriptionID, options?: { limit?: number; next?: CursorToken }): Promise<GetSubscriptionEventsResponse> {
    return APIService.inst.apiFetch<void, GetSubscriptionEventsResponse>('GET', '/users/:uid/subscriptions/:sid/events', {
      urlParams: { uid: userId, sid: subscriptionId },
      queryParams: {
        limit: options?.limit,
        next: options?.next
      },
      auth: true
    });
  }

  // List all subscriptions (admin only)
  static async listAllSubscriptions(options?: { limit?: number; next?: CursorToken, status?: SubscriptionStatus|SubscriptionStatus[]; }): Promise<ListAllSubscriptionsResponse> {
    return APIService.inst.apiFetch<void, ListAllSubscriptionsResponse>('GET', '/subscriptions', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        status: options?.status
      },
      auth: true
    });
  }

  // Get subscription products (public)
  static async getSubscriptionProducts(): Promise<GetSubscriptionProductsResponse> {
    return APIService.inst.apiFetch<void, GetSubscriptionProductsResponse>('GET', '/subscriptions/products', {
      auth: false
    });
  }

  // Get single subscription product (public)
  static async getSubscriptionProduct(subscriptionType: SubscriptionType): Promise<SubscriptionPriceProduct> {
    return APIService.inst.apiFetch<void, SubscriptionPriceProduct>('GET', '/subscriptions/products/:type', {
      urlParams: { type: subscriptionType },
      auth: false
    });
  }

  // Create an active subscription for a user (admin only - for external subscriptions)
  static async createActiveSubscription(userId: UserID, request: CreateActiveSubscriptionRequest): Promise<void> {
    return APIService.inst.apiFetch<CreateActiveSubscriptionRequest, void>('POST', '/users/:uid/subscriptions/create-active', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  // Get Stripe billing portal link for a user
  static async getStripeLink(userId: UserID): Promise<{ link: string | null }> {
    return APIService.inst.apiFetch<void, { link: string | null }>('GET', '/users/:uid/stripe-link', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  // ============================================
  // Events (Calendar)
  // ============================================

  static async listEvents(options?: ListEventsOptions): Promise<ListEventsResponse> {
    return APIService.inst.apiFetch<void, ListEventsResponse>('GET', '/events', {
      queryParams: {
        pageSize: options?.pageSize,
        next: options?.next,
        futureOrToday: options?.futureOrToday,
        feature: options?.feature,
        seriesID: options?.seriesID
      },
      auth: true
    });
  }

  static async getEvent(eventId: EventID): Promise<Event> {
    return APIService.inst.apiFetch<void, Event>('GET', '/events/:eventId', {
      urlParams: { eventId },
      auth: true
    });
  }

  static async createEvent(request: CreateEventRequest): Promise<Event> {
    return APIService.inst.apiFetch<CreateEventRequest, Event>('POST', '/events', {
      body: request,
      auth: true
    });
  }

  // scope FOLLOWING/ALL only work on events belonging to a series and reject start/end changes
  static async updateEvent(eventId: EventID, request: UpdateEventRequest, scope?: EventSeriesScope): Promise<Event> {
    return APIService.inst.apiFetch<UpdateEventRequest, Event>('PATCH', '/events/:eventId', {
      urlParams: { eventId },
      queryParams: { scope },
      body: request,
      auth: true
    });
  }

  // scope FOLLOWING also shortens the series' until-date, ALL deletes the whole series
  static async deleteEvent(eventId: EventID, scope?: EventSeriesScope): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/events/:eventId', {
      urlParams: { eventId },
      queryParams: { scope },
      auth: true
    });
  }

  // Imports calendar events from a semicolon-separated csv sent as the raw request body.
  // With dryRun the rows are only validated and reported, nothing is written.
  // Pass the File/Blob rather than a decoded string - the backend detects UTF-8 vs
  // Windows-1252 itself, and decoding here would corrupt Windows-1252 umlauts.
  static async importEvents(csv: Blob | string, dryRun: boolean): Promise<EventImportResult> {
    return APIService.inst.apiFetchRaw<EventImportResult>('POST', '/events/import', {
      rawBody: csv,
      contentType: 'text/csv',
      queryParams: { dryRun },
      auth: true
    });
  }

  // ============================================
  // Event Series (recurring events)
  // ============================================

  static async listEventSeries(options?: ListEventSeriesOptions): Promise<ListEventSeriesResponse> {
    return APIService.inst.apiFetch<void, ListEventSeriesResponse>('GET', '/event-series', {
      queryParams: {
        pageSize: options?.pageSize,
        next: options?.next,
        activeOnly: options?.activeOnly,
        sort: options?.sort,
        sortDirection: options?.sortDirection
      },
      auth: true
    });
  }

  static async getEventSeries(seriesId: EventSeriesID): Promise<EventSeries> {
    return APIService.inst.apiFetch<void, EventSeries>('GET', '/event-series/:seriesId', {
      urlParams: { seriesId },
      auth: true
    });
  }

  static async createEventSeries(request: CreateEventSeriesRequest): Promise<EventSeriesMutationResponse> {
    return APIService.inst.apiFetch<CreateEventSeriesRequest, EventSeriesMutationResponse>('POST', '/event-series', {
      body: request,
      auth: true
    });
  }

  // Only send fields that actually changed - the backend decides whether to rebuild all
  // future occurrences from the presence of start/end/until/rule in the body.
  static async updateEventSeries(seriesId: EventSeriesID, request: UpdateEventSeriesRequest): Promise<UpdateEventSeriesResponse> {
    return APIService.inst.apiFetch<UpdateEventSeriesRequest, UpdateEventSeriesResponse>('PATCH', '/event-series/:seriesId', {
      urlParams: { seriesId },
      body: request,
      auth: true
    });
  }

  // Discards all future events of the series (including individually edited ones)
  // and recreates them from the current recurrence rule
  static async regenerateEventSeries(seriesId: EventSeriesID): Promise<EventSeriesMutationResponse> {
    return APIService.inst.apiFetch<void, EventSeriesMutationResponse>('POST', '/event-series/:seriesId/regenerate', {
      urlParams: { seriesId },
      auth: true
    });
  }

  // Past occurrences are kept by default so the calendar history stays intact
  static async deleteEventSeries(seriesId: EventSeriesID, includePast?: boolean): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/event-series/:seriesId', {
      urlParams: { seriesId },
      queryParams: { includePast },
      auth: true
    });
  }

  // ============================================
  // Admin Impersonation
  // ============================================

  /**
   * Get an authentication token for a specific user (admin only).
   * Used for admin impersonation feature.
   */
  static async getUserToken(userId: UserID): Promise<{ token: string; user: User }> {
    return APIService.inst.apiFetch<void, { token: string; user: User }>('POST', '/users/:uid/token', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  // ============================================
  // Stock Exchanges
  // ============================================

  static async listStockExchanges(options?: ListStockExchangesOptions): Promise<ListStockExchangesResponse> {
    return APIService.inst.apiFetch<void, ListStockExchangesResponse>('GET', '/stock-exchanges', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        identifier: options?.identifier,
        name: options?.name,
        sort: options?.sort,
        sortDirection: options?.sortDirection
      },
      auth: true
    });
  }

  static async getStockExchange(id: StockExchangeID | string): Promise<StockExchange> {
    return APIService.inst.apiFetch<void, StockExchange>('GET', '/stock-exchanges/:seid', {
      urlParams: { seid: id },
      auth: true
    });
  }

  static async createStockExchange(request: CreateStockExchangeRequest): Promise<StockExchange> {
    return APIService.inst.apiFetch<CreateStockExchangeRequest, StockExchange>('POST', '/stock-exchanges', {
      body: request,
      auth: true
    });
  }

  static async updateStockExchange(id: StockExchangeID, request: UpdateStockExchangeRequest): Promise<StockExchange> {
    return APIService.inst.apiFetch<UpdateStockExchangeRequest, StockExchange>('PATCH', '/stock-exchanges/:seid', {
      urlParams: { seid: id },
      body: request,
      auth: true
    });
  }

  static async deleteStockExchange(id: StockExchangeID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/stock-exchanges/:seid', {
      urlParams: { seid: id },
      auth: true
    });
  }

  // ============================================
  // User Questions
  // ============================================

  static async createUserQuestion(request: CreateUserQuestionRequest): Promise<UserQuestion> {
    return APIService.inst.apiFetch<CreateUserQuestionRequest, UserQuestion>('POST', '/users/self/questions', {
      body: request,
      auth: true
    });
  }

  static async listUserQuestions(options?: ListUserQuestionsOptions): Promise<ListUserQuestionsResponse> {
    return APIService.inst.apiFetch<void, ListUserQuestionsResponse>('GET', '/questions', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        status: options?.status,
        userID: options?.userID,
        search: options?.search,
        sort: options?.sort,
        sortDirection: options?.sortDirection
      },
      auth: true
    });
  }

  static async getUserQuestion(id: UserQuestionID): Promise<UserQuestion> {
    return APIService.inst.apiFetch<void, UserQuestion>('GET', '/questions/:qid', {
      urlParams: { qid: id },
      auth: true
    });
  }

  static async updateUserQuestion(id: UserQuestionID, request: UpdateUserQuestionRequest): Promise<UserQuestion> {
    return APIService.inst.apiFetch<UpdateUserQuestionRequest, UserQuestion>('PATCH', '/questions/:qid', {
      urlParams: { qid: id },
      body: request,
      auth: true
    });
  }

  static async deleteUserQuestion(id: UserQuestionID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/questions/:qid', {
      urlParams: { qid: id },
      auth: true
    });
  }

  // ============================================
  // Resend Notifications
  // ============================================

  static async resendCoreRecommendations(request: ResendNotificationsRequest, dryRun: boolean): Promise<ResendCoreRecommendationsResponse> {
    return APIService.inst.apiFetch<ResendNotificationsRequest, ResendCoreRecommendationsResponse>('POST', '/recommendations/core/resend', {
      body: request,
      queryParams: { dryRun },
      auth: true
    });
  }

  static async resendEarningsRecommendations(request: ResendNotificationsRequest, dryRun: boolean): Promise<ResendEarningsRecommendationsResponse> {
    return APIService.inst.apiFetch<ResendNotificationsRequest, ResendEarningsRecommendationsResponse>('POST', '/recommendations/earnings/resend', {
      body: request,
      queryParams: { dryRun },
      auth: true
    });
  }

  static async resendSwingTradeRecommendations(request: ResendNotificationsRequest, dryRun: boolean): Promise<ResendSwingTradesResponse> {
    return APIService.inst.apiFetch<ResendNotificationsRequest, ResendSwingTradesResponse>('POST', '/recommendations/swing-trades/resend', {
      body: request,
      queryParams: { dryRun },
      auth: true
    });
  }

  static async resendLongTermRecommendations(request: ResendNotificationsRequest, dryRun: boolean): Promise<ResendLongTermTradesResponse> {
    return APIService.inst.apiFetch<ResendNotificationsRequest, ResendLongTermTradesResponse>('POST', '/recommendations/long-term/resend', {
      body: request,
      queryParams: { dryRun },
      auth: true
    });
  }

  // Day Trading API routes

  static async listDayTradingSessions(options?: ListDayTradingSessionsOptions): Promise<ListDayTradingSessionsResponse> {
    return APIService.inst.apiFetch<void, ListDayTradingSessionsResponse>('GET', '/day-trading/sessions', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        open: options?.open,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
        since: options?.since,
        until: options?.until,
      },
      auth: true
    });
  }

  static async getDayTradingSession(dtsid: DayTradingSessionID, updateModPing: boolean = false, updateParticipPing: boolean = false): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('GET', '/day-trading/sessions/:dtsid', {
      urlParams: { dtsid },
      queryParams: {
        moderatorPing:   updateModPing,
        participantPing: updateParticipPing,
      },
      auth: true
    });
  }

  static async createDayTradingSession(request: CreateDayTradingSessionRequest): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<CreateDayTradingSessionRequest, DayTradingSession>('POST', '/day-trading/sessions', {
      body: request,
      auth: true
    });
  }

  static async updateDayTradingSession(dtsid: DayTradingSessionID, request: UpdateDayTradingSessionRequest): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<UpdateDayTradingSessionRequest, DayTradingSession>('PATCH', '/day-trading/sessions/:dtsid', {
      urlParams: { dtsid },
      body: request,
      auth: true
    });
  }

  static async regenerateDayTradingSessionStreamKey(dtsid: DayTradingSessionID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/regenerate-stream-key', {
      urlParams: { dtsid },
      auth: true
    });
  }

  static async getDayTradingSessionStreamStatus(dtsid: DayTradingSessionID): Promise<DayTradingStreamStatusResponse> {
    return APIService.inst.apiFetch<void, DayTradingStreamStatusResponse>('GET', '/day-trading/sessions/:dtsid/stream-status', {
      urlParams: { dtsid },
      auth: true
    });
  }

  static async deleteDayTradingSession(dtsid: DayTradingSessionID): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/day-trading/sessions/:dtsid', {
      urlParams: { dtsid },
      auth: true
    });
  }

  static async joinDayTradingSessionAsModerator(dtsid: DayTradingSessionID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/join', {
      urlParams: { dtsid },
      queryParams: { participate: true },
      auth: true
    });
  }

  static async closeDayTradingSession(dtsid: DayTradingSessionID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/close', {
      urlParams: { dtsid },
      auth: true
    });
  }

  static async createDayTrade(dtsid: DayTradingSessionID, request: CreateDayTradeRequest): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<CreateDayTradeRequest, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/trades', {
      urlParams: { dtsid },
      body: request,
      auth: true
    });
  }

  static async updateDayTrade(dtsid: DayTradingSessionID, dtrid: DayTradeID, request: UpdateDayTradeRequest): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<UpdateDayTradeRequest, DayTradingSession>('PATCH', '/day-trading/sessions/:dtsid/trades/:dtrid', {
      urlParams: { dtsid, dtrid },
      body: request,
      auth: true
    });
  }

  static async deleteDayTrade(dtsid: DayTradingSessionID, dtrid: DayTradeID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('DELETE', '/day-trading/sessions/:dtsid/trades/:dtrid', {
      urlParams: { dtsid, dtrid },
      auth: true
    });
  }

  static async createDayTradeAdjustement(dtsid: DayTradingSessionID, dtrid: DayTradeID, request: CreateDayTradeAdjustementRequest): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<CreateDayTradeAdjustementRequest, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/trades/:dtrid/adjustements', {
      urlParams: { dtsid, dtrid },
      body: request,
      auth: true
    });
  }

  static async updateDayTradeAdjustement(dtsid: DayTradingSessionID, dtrid: DayTradeID, dtaid: DayTradeAdjustementID, request: UpdateDayTradeAdjustementRequest): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<UpdateDayTradeAdjustementRequest, DayTradingSession>('PATCH', '/day-trading/sessions/:dtsid/trades/:dtrid/adjustements/:dtaid', {
      urlParams: { dtsid, dtrid, dtaid },
      body: request,
      auth: true
    });
  }

  static async deleteDayTradeAdjustement(dtsid: DayTradingSessionID, dtrid: DayTradeID, dtaid: DayTradeAdjustementID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('DELETE', '/day-trading/sessions/:dtsid/trades/:dtrid/adjustements/:dtaid', {
      urlParams: { dtsid, dtrid, dtaid },
      auth: true
    });
  }

  static async joinDayTradingSessionAsParticipant(dtsid: DayTradingSessionID): Promise<DayTradingParticipant> {
    return APIService.inst.apiFetch<void, DayTradingParticipant>('POST', '/day-trading/sessions/:dtsid/participants', {
      urlParams: { dtsid },
      auth: true
    });
  }

  static async getDayTradingParticipant(dtsid: DayTradingSessionID, dtpid: DayTradingParticipantID|'self'): Promise<DayTradingParticipant> {
    return APIService.inst.apiFetch<void, DayTradingParticipant>('GET', '/day-trading/sessions/:dtsid/participants/:dtpid', {
      urlParams: { dtsid, dtpid },
      auth: true
    });
  }

  static async updateDayTradingParticipantState(dtsid: DayTradingSessionID, dtpid: DayTradingParticipantID|'self', request: UpdateDayTradingParticipantStateRequest): Promise<DayTradingParticipant> {
    return APIService.inst.apiFetch<UpdateDayTradingParticipantStateRequest, DayTradingParticipant>('PATCH', '/day-trading/sessions/:dtsid/participants/:dtpid/state', {
      urlParams: { dtsid, dtpid },
      body: request,
      auth: true
    });
  }

  static async sendDayTradingChatMessage(dtsid: DayTradingSessionID, args: { text?: string; image?: File }): Promise<DayTradingSession> {
    return APIService.inst.apiFetchForm<DayTradingSession>('POST', '/day-trading/sessions/:dtsid/chat/messages', {
      urlParams: { dtsid },
      fields: {
        text:  (args.text !== undefined && args.text !== '') ? args.text : undefined,
        image: args.image,
      },
      auth: true
    });
  }

  static async editDayTradingChatMessage(dtsid: DayTradingSessionID, dtcmid: DayTradingChatMessageID, text: string): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<{ text: string }, DayTradingSession>('PATCH', '/day-trading/sessions/:dtsid/chat/messages/:dtcmid', {
      urlParams: { dtsid, dtcmid },
      body: { text },
      auth: true
    });
  }

  static async deleteDayTradingChatMessage(dtsid: DayTradingSessionID, dtcmid: DayTradingChatMessageID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('DELETE', '/day-trading/sessions/:dtsid/chat/messages/:dtcmid', {
      urlParams: { dtsid, dtcmid },
      auth: true
    });
  }

  static async reactToDayTradingChatMessage(dtsid: DayTradingSessionID, dtcmid: DayTradingChatMessageID, emoji: number): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<{ emoji: number }, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/chat/messages/:dtcmid/reactions', {
      urlParams: { dtsid, dtcmid },
      body: { emoji },
      auth: true
    });
  }

  static async removeDayTradingChatReaction(dtsid: DayTradingSessionID, dtcmid: DayTradingChatMessageID, dtcrid: DayTradingChatReactionID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('DELETE', '/day-trading/sessions/:dtsid/chat/messages/:dtcmid/reactions/:dtcrid', {
      urlParams: { dtsid, dtcmid, dtcrid },
      auth: true
    });
  }

  static async markDayTradingChatMessageViewed(dtsid: DayTradingSessionID, dtcmid: DayTradingChatMessageID): Promise<DayTradingSession> {
    return APIService.inst.apiFetch<void, DayTradingSession>('POST', '/day-trading/sessions/:dtsid/chat/messages/:dtcmid/view', {
      urlParams: { dtsid, dtcmid },
      auth: true
    });
  }

  // ===== Option Screener =====

  static async listOptionContracts(options?: ListOptionContractsOptions): Promise<ListOptionContractsResponse> {
    return APIService.inst.apiFetch<void, ListOptionContractsResponse>('GET', '/option-screener/options', {
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        symbolID: options?.symbolID,
        symbolTicker: options?.symbolTicker,
        search: options?.search,
        right: options?.right,
        moneyness: options?.moneyness,
        expirationFrom: options?.expirationFrom,
        expirationTo: options?.expirationTo,
        dteMin: options?.dteMin,
        dteMax: options?.dteMax,
        strikeMin: options?.strikeMin,
        strikeMax: options?.strikeMax,
        ivMin: options?.ivMin,
        ivMax: options?.ivMax,
        deltaMin: options?.deltaMin,
        deltaMax: options?.deltaMax,
        volumeMin: options?.volumeMin,
        openInterestMin: options?.openInterestMin,
        midMin: options?.midMin,
        midMax: options?.midMax,
        bidMin: options?.bidMin,
        bidMax: options?.bidMax,
        askMin: options?.askMin,
        askMax: options?.askMax,
        yieldMin: options?.yieldMin,
        yieldMax: options?.yieldMax,
        maxSpreadPct: options?.maxSpreadPct,
        sort: options?.sort,
        sortDirection: options?.sortDirection,
      },
      auth: true
    });
  }

  static async getOptionContract(id: OptionContractID): Promise<OptionContract> {
    return APIService.inst.apiFetch<void, OptionContract>('GET', '/option-screener/options/:id', {
      urlParams: { id },
      auth: true
    });
  }

  static async listOptionUnderlyings(): Promise<ListOptionUnderlyingsResponse> {
    return APIService.inst.apiFetch<void, ListOptionUnderlyingsResponse>('GET', '/option-screener/underlyings', {
      auth: true
    });
  }

  // ---------------------------------------------------------------------------
  // Trading journal
  // ---------------------------------------------------------------------------
  // The backend routes are /users/:uid/journal/... and allow self-or-admin access,
  // so every method takes an optional trailing userId (defaults to the current user).

  static async listJournalOptionTrades(options?: ListJournalOptionTradesOptions, userId: string = 'self'): Promise<ListJournalOptionTradesResponse> {
    return APIService.inst.apiFetch<void, ListJournalOptionTradesResponse>('GET', '/users/:uid/journal/option-trades', {
      urlParams: { uid: userId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        status: options?.status,
        strategy: options?.strategy,
        right: options?.right,
        symbolTicker: options?.symbolTicker,
      },
      auth: true
    });
  }

  static async getJournalOptionTrade(tradeId: string, userId: string = 'self'): Promise<JournalOptionTrade> {
    return APIService.inst.apiFetch<void, JournalOptionTrade>('GET', '/users/:uid/journal/option-trades/:tradeId', {
      urlParams: { uid: userId, tradeId },
      auth: true
    });
  }

  static async createJournalOptionTrade(request: CreateJournalOptionTradeRequest, userId: string = 'self'): Promise<JournalOptionTrade> {
    return APIService.inst.apiFetch<CreateJournalOptionTradeRequest, JournalOptionTrade>('POST', '/users/:uid/journal/option-trades', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async updateJournalOptionTrade(tradeId: string, request: UpdateJournalOptionTradeRequest, userId: string = 'self'): Promise<JournalOptionTrade> {
    return APIService.inst.apiFetch<UpdateJournalOptionTradeRequest, JournalOptionTrade>('PATCH', '/users/:uid/journal/option-trades/:tradeId', {
      urlParams: { uid: userId, tradeId },
      body: request,
      auth: true
    });
  }

  static async deleteJournalOptionTrade(tradeId: string, userId: string = 'self'): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid/journal/option-trades/:tradeId', {
      urlParams: { uid: userId, tradeId },
      auth: true
    });
  }

  static async assignJournalOptionTrade(tradeId: string, request: AssignJournalOptionTradeRequest, userId: string = 'self'): Promise<AssignJournalOptionTradeResponse> {
    return APIService.inst.apiFetch<AssignJournalOptionTradeRequest, AssignJournalOptionTradeResponse>('POST', '/users/:uid/journal/option-trades/:tradeId/assign', {
      urlParams: { uid: userId, tradeId },
      body: request,
      auth: true
    });
  }

  static async listJournalStockPositions(options?: ListJournalStockPositionsOptions, userId: string = 'self'): Promise<ListJournalStockPositionsResponse> {
    return APIService.inst.apiFetch<void, ListJournalStockPositionsResponse>('GET', '/users/:uid/journal/stock-positions', {
      urlParams: { uid: userId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        status: options?.status,
        symbolTicker: options?.symbolTicker,
        assignedOnly: options?.assignedOnly,
      },
      auth: true
    });
  }

  static async updateJournalStockPosition(positionId: string, request: UpdateJournalStockPositionRequest, userId: string = 'self'): Promise<JournalStockPosition> {
    return APIService.inst.apiFetch<UpdateJournalStockPositionRequest, JournalStockPosition>('PATCH', '/users/:uid/journal/stock-positions/:positionId', {
      urlParams: { uid: userId, positionId },
      body: request,
      auth: true
    });
  }

  static async deleteJournalStockPosition(positionId: string, userId: string = 'self'): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid/journal/stock-positions/:positionId', {
      urlParams: { uid: userId, positionId },
      auth: true
    });
  }

  static async getJournalStats(userId: string = 'self'): Promise<JournalStats> {
    return APIService.inst.apiFetch<void, JournalStats>('GET', '/users/:uid/journal/stats', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async getJournalCalendar(from: string, to: string, userId: string = 'self'): Promise<JournalCalendar> {
    return APIService.inst.apiFetch<void, JournalCalendar>('GET', '/users/:uid/journal/stats/calendar', {
      urlParams: { uid: userId },
      queryParams: { from, to },
      auth: true
    });
  }

  static async getJournalCapitalLadder(userId: string = 'self'): Promise<JournalCapitalLadder> {
    return APIService.inst.apiFetch<void, JournalCapitalLadder>('GET', '/users/:uid/journal/capital-ladder', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async generateJournalSummary(periodType: string, period: string, userId: string = 'self'): Promise<JournalSummary> {
    return APIService.inst.apiFetch<{ periodType: string; period: string }, JournalSummary>('POST', '/users/:uid/journal/ai-summary', {
      urlParams: { uid: userId },
      body: { periodType, period },
      auth: true
    });
  }

  static async listJournalTags(userId: string = 'self'): Promise<ListJournalTagsResponse> {
    return APIService.inst.apiFetch<void, ListJournalTagsResponse>('GET', '/users/:uid/journal/tags', {
      urlParams: { uid: userId },
      queryParams: { limit: 200 },
      auth: true
    });
  }

  static async createJournalTag(request: CreateJournalTagRequest, userId: string = 'self'): Promise<JournalTag> {
    return APIService.inst.apiFetch<CreateJournalTagRequest, JournalTag>('POST', '/users/:uid/journal/tags', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async updateJournalTag(tagId: string, request: UpdateJournalTagRequest, userId: string = 'self'): Promise<JournalTag> {
    return APIService.inst.apiFetch<UpdateJournalTagRequest, JournalTag>('PATCH', '/users/:uid/journal/tags/:tagId', {
      urlParams: { uid: userId, tagId },
      body: request,
      auth: true
    });
  }

  static async deleteJournalTag(tagId: string, userId: string = 'self'): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid/journal/tags/:tagId', {
      urlParams: { uid: userId, tagId },
      auth: true
    });
  }

  static async listJournalTemplates(userId: string = 'self'): Promise<ListJournalTemplatesResponse> {
    return APIService.inst.apiFetch<void, ListJournalTemplatesResponse>('GET', '/users/:uid/journal/templates', {
      urlParams: { uid: userId },
      queryParams: { limit: 100, sort: 'sortOrder', sortDirection: 'ASC' },
      auth: true
    });
  }

  static async createJournalTemplate(request: CreateJournalTemplateRequest, userId: string = 'self'): Promise<JournalTemplate> {
    return APIService.inst.apiFetch<CreateJournalTemplateRequest, JournalTemplate>('POST', '/users/:uid/journal/templates', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async updateJournalTemplate(templateId: string, request: UpdateJournalTemplateRequest, userId: string = 'self'): Promise<JournalTemplate> {
    return APIService.inst.apiFetch<UpdateJournalTemplateRequest, JournalTemplate>('PATCH', '/users/:uid/journal/templates/:templateId', {
      urlParams: { uid: userId, templateId },
      body: request,
      auth: true
    });
  }

  static async deleteJournalTemplate(templateId: string, userId: string = 'self'): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid/journal/templates/:templateId', {
      urlParams: { uid: userId, templateId },
      auth: true
    });
  }

  static async listJournalReviews(options?: ListJournalReviewsOptions, userId: string = 'self'): Promise<ListJournalReviewsResponse> {
    return APIService.inst.apiFetch<void, ListJournalReviewsResponse>('GET', '/users/:uid/journal/reviews', {
      urlParams: { uid: userId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        status: options?.status,
      },
      auth: true
    });
  }

  static async createJournalReview(request: CreateJournalReviewRequest, userId: string = 'self'): Promise<JournalReview> {
    return APIService.inst.apiFetch<CreateJournalReviewRequest, JournalReview>('POST', '/users/:uid/journal/reviews', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async updateJournalReview(reviewId: string, request: UpdateJournalReviewRequest, userId: string = 'self'): Promise<JournalReview> {
    return APIService.inst.apiFetch<UpdateJournalReviewRequest, JournalReview>('PATCH', '/users/:uid/journal/reviews/:reviewId', {
      urlParams: { uid: userId, reviewId },
      body: request,
      auth: true
    });
  }

  static async deleteJournalReview(reviewId: string, userId: string = 'self'): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid/journal/reviews/:reviewId', {
      urlParams: { uid: userId, reviewId },
      auth: true
    });
  }

  static async getJournalSettings(userId: string = 'self'): Promise<JournalSettingsResponse> {
    return APIService.inst.apiFetch<void, JournalSettingsResponse>('GET', '/users/:uid/journal/settings', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async updateJournalSettings(request: UpdateJournalSettingsRequest, userId: string = 'self'): Promise<JournalSettingsResponse> {
    return APIService.inst.apiFetch<UpdateJournalSettingsRequest, JournalSettingsResponse>('PATCH', '/users/:uid/journal/settings', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async getJournalOverview(userId: string = 'self'): Promise<JournalOverview> {
    return APIService.inst.apiFetch<void, JournalOverview>('GET', '/users/:uid/journal/overview', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  // ---------------------------------------------------------------------------
  // Trading journal - Interactive Brokers import
  // ---------------------------------------------------------------------------

  static async getJournalIBConfig(userId: string = 'self'): Promise<JournalIBConfigResponse> {
    return APIService.inst.apiFetch<void, JournalIBConfigResponse>('GET', '/users/:uid/journal/ib/config', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  static async updateJournalIBConfig(request: UpdateJournalIBConfigRequest, userId: string = 'self'): Promise<JournalIBConfigResponse> {
    return APIService.inst.apiFetch<UpdateJournalIBConfigRequest, JournalIBConfigResponse>('PUT', '/users/:uid/journal/ib/config', {
      urlParams: { uid: userId },
      body: request,
      auth: true
    });
  }

  static async disconnectJournalIB(userId: string = 'self'): Promise<void> {
    return APIService.inst.apiFetch<void, void>('DELETE', '/users/:uid/journal/ib/config', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  // Blocks for roughly 30 seconds: IB needs that long between the statement request
  // and its delivery, so this gets a much longer deadline than a normal call.
  static async syncJournalIB(userId: string = 'self'): Promise<JournalIBImportResult> {
    return APIService.inst.apiFetch<void, JournalIBImportResult>('POST', '/users/:uid/journal/ib/sync', {
      urlParams: { uid: userId },
      timeoutMs: JOURNAL_IB_SYNC_TIMEOUT_MS,
      auth: true
    });
  }

  static async importJournalIBFile(file: File, userId: string = 'self'): Promise<JournalIBImportResult> {
    return APIService.inst.apiFetchForm<JournalIBImportResult>('POST', '/users/:uid/journal/ib/import-file', {
      urlParams: { uid: userId },
      fields: { file },
      timeoutMs: JOURNAL_IB_SYNC_TIMEOUT_MS,
      auth: true
    });
  }

  static async listJournalIBSyncRuns(options?: ListJournalIBSyncRunsOptions, userId: string = 'self'): Promise<ListJournalIBSyncRunsResponse> {
    return APIService.inst.apiFetch<void, ListJournalIBSyncRunsResponse>('GET', '/users/:uid/journal/ib/runs', {
      urlParams: { uid: userId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
      },
      auth: true
    });
  }

  static async listJournalIBExecutions(options?: ListJournalIBExecutionsOptions, userId: string = 'self'): Promise<ListJournalIBExecutionsResponse> {
    return APIService.inst.apiFetch<void, ListJournalIBExecutionsResponse>('GET', '/users/:uid/journal/ib/executions', {
      urlParams: { uid: userId },
      queryParams: {
        limit: options?.limit,
        next: options?.next,
        accountID: options?.accountID,
        assetCategory: options?.assetCategory,
      },
      auth: true
    });
  }

  static async listJournalIBUnknownSymbols(userId: string = 'self'): Promise<ListJournalIBUnknownSymbolsResponse> {
    return APIService.inst.apiFetch<void, ListJournalIBUnknownSymbolsResponse>('GET', '/users/:uid/journal/ib/unknown-symbols', {
      urlParams: { uid: userId },
      auth: true
    });
  }

  // Aggregated across all users, admin only - the list of symbols the journal imports are missing.
  static async listAllJournalIBUnknownSymbols(): Promise<ListJournalIBUnknownSymbolsResponse> {
    return APIService.inst.apiFetch<void, ListJournalIBUnknownSymbolsResponse>('GET', '/journal/ib/unknown-symbols', {
      auth: true
    });
  }
}
